// src/pages/quotation/QuotationForm.jsx
// ✅ 용역 견적서 관리 [1단계 단계형 UI → 팝업 선택 방식 전환] — 좌측 입력 / 우측
// 실시간 계산 미리보기(전 단계 공통 유지). 신규(/quotation/new)와 편집(/quotation/:id/edit)
// 겸용 — 신규는 발주처를 새로 정하고, 편집은 기존 견적서 버전(주로 draft)의 값을
// 불러와 그 자리에서 저장한다(같은 id 유지, final이면 저장 시 백엔드가 자동으로
// 새 draft 버전을 만들어 그쪽으로 이동).
//
// ✅ Step1 팝업화 — 신규 작성 진입 시 팝업 시퀀스 자동 시작(①연도→②발주처→
// ③근무지→④분야), 완료 후 본 화면에 요약(연도·발주처·근무지·분야) + 각 항목
// [변경] 버튼(해당 팝업만 재호출, 시퀀스 강제 아님 — ESC/닫기로 중단해도 이전
// 선택은 유지된다). 품명은 팝업 대상이 아니라 본 화면에서 계속 자동 조립+직접
// 수정 가능(titleTouched 가드, 기존 로직 그대로). 편집(isEdit) 모드는 팝업
// 시퀀스를 자동 시작하지 않는다(발주처는 편집 불가 — Quotation 저장은 이미
// 만들어진 QuotationCase에 종속). 근무지/분야 [변경]은 편집 모드에서도 계속
// 쓸 수 있게 열어둠(교체 편의).
//
// Step1(작성): 위 팝업 요약 + 계약개월/시급/보험료율 + 근무조건 그룹 카드(구
// Step2 내용 병합 — "분야 다중선택" 버튼 자체는 ④ 팝업 안으로 옮겨서 화면
// 밀도를 낮췄다). Step2(확인·저장): 구 Step3 그대로.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useBackdropClose from "../../hooks/useBackdropClose";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api";
import { downloadFileWithAlert } from "../../utils/download";
import { fmtMonths } from "./format";

const ALLOWANCE_CATEGORIES = [
  { value: "overtime", label: "연장근로수당" },
  { value: "night", label: "야간근로수당" },
  { value: "holiday", label: "휴일근무수당" },
  { value: "leader", label: "반장수당" },
  { value: "annual", label: "연차수당" },
  { value: "transport", label: "교통보조비" },
];

// ✅ 제수당 6항목의 입력 방식은 실무 규칙으로 고정한다 — 화면에서 바꿀 수 없게 해
// 오입력을 구조적으로 차단(÷개월 칸을 없앤 것과 같은 방식).
//   mode        입력 방식 고정("param"/"fixed"). 없으면 정액/파라미터 선택 가능.
//   multiplier  fixed=고정 배율(읽기전용) / options=선택지 + default 기본값.
//               둘 다 없으면 자유 입력.
const ALLOWANCE_RULES = {
  overtime:  { mode: "param", multiplier: { options: [1.5, 1], default: 1.5 } },
  night:     { mode: "param", multiplier: { fixed: 0.5 } },
  holiday:   { mode: "param", multiplier: { options: [1.5, 1], default: 1.5 } },
  leader:    { mode: "fixed" },
  // ✅ 연차는 정액형/파라미터형 토글을 없애고 파라미터형으로 고정(사용자 확정
  // 2026-08-20). 연차수당은 "연차일수 × 1일 시간 × 시급"으로 정해지는 값이라
  // 정액을 직접 적는 자리가 아니다. 기존 저장분에 정액형은 0건(실측)이라
  // 하위호환 문제도 없다 — 만에 하나 있어도 violatesRule이 경고를 띄우고
  // 재저장 시 normalizeAllowance가 param으로 정정한다.
  // params: 연차는 시간·일수를 사업장마다 직접 넣는 값이라 기본 0으로 비워 둔다
  // (다른 항목의 기본 일수 1은 그대로).
  // weeks:false — ×주 입력을 감추고 param_weeks를 1로 고정한다. 연차는
  // "시간×일수÷계약개월"이라 주 단위 반복이 성립하지 않는다(사용자 확정 2026-08-20).
  // ⚠️ 백엔드도 같은 규칙을 강제한다(calc: category=="annual"이면 weeks=1) —
  // 이미 저장된 견적서는 화면을 고쳐도 못 막기 때문이다. 한쪽만 바꾸지 말 것.
  annual:    { mode: "param", multiplier: { fixed: 1 }, params: { hours: 0, days: 0 }, weeks: false },
  transport: {},   // 현행 유지 — 정액/파라미터 선택 가능, 배율 자유 입력
};

// ✅ ×주(월 환산 주수) 프리셋 — 백엔드 calc.WEEKS_PRESETS와 같은 값.
// 구성 문구("4.345주(매주)" / "2.1725주(격주, 4.345÷2)")는 백엔드가 만든다 —
// 미리보기·엑셀이 같은 소스를 보게 하려고 화면에서 문구를 따로 만들지 않는다.
const WEEKS_PRESETS = [
  { value: 4.345, label: "4.345", sub: "매주" },
  { value: 2.1725, label: "2.1725", sub: "격주" },
];
// ✅ param_weeks는 모델이 소수 4자리(DecimalField decimal_places=4)다 — 격주
// 프리셋 4.345÷2 = 2.1725가 정확히 4자리라 그 자리수에 맞춰져 있다.
// 직접입력 칸에 2.17253처럼 더 긴 소수를 타이핑하면 저장이 400으로 막히므로
// 전송 직전에 같은 자리수로 맞춘다(모델 자리수와 이 함수를 함께 고칠 것).
const roundWeeks = (v) => Math.round((Number(v) || 0) * 10000) / 10000;

const isWeeksPreset = (v) => WEEKS_PRESETS.some((w) => Number(v) === w.value);

// 1배는 감시적·단속적 근로자(근로기준법 63조 적용 제외) 케이스라 부기를 붙인다.
const MULTIPLIER_LABEL = {
  1.5: { main: "1.5배", sub: "일반" },
  1:   { main: "1배", sub: "감시적 근로자 — 근로기준법 63조 적용 제외" },
};

const ruleFor = (category) => ALLOWANCE_RULES[category] || {};

/** 저장 직전 정규화 — 고정 규칙이 있으면 그 값으로 정정해서 보낸다.
 *  옛 데이터(예: 연장 2배)가 재저장 시 규칙대로 바로잡히는 경로가 여기다. */
const normalizeAllowance = (a) => {
  const rule = ruleFor(a.category);
  const out = { ...a };
  if (rule.mode) out.input_mode = rule.mode;
  const m = rule.multiplier;
  if (m) {
    if (m.fixed !== undefined) out.param_multiplier = m.fixed;
    else if (m.options && !m.options.includes(Number(a.param_multiplier))) {
      out.param_multiplier = m.default;
    }
  }
  if (rule.weeks === false) out.param_weeks = 1;
  return out;
};

/** 저장값이 규칙과 다른가(옛 데이터 표시용). 정규화 결과와 비교해 판정한다. */
const violatesRule = (a) => {
  const n = normalizeAllowance(a);
  return n.input_mode !== a.input_mode
    || Number(n.param_multiplier) !== Number(a.param_multiplier)
    || Number(n.param_weeks ?? 1) !== Number(a.param_weeks ?? 1);
};

const defaultAllowanceItems = () => ALLOWANCE_CATEGORIES.map((c) => {
  const rule = ruleFor(c.value);
  const m = rule.multiplier;
  return normalizeAllowance({
    category: c.value, input_mode: rule.mode || "fixed",
    param_hours: rule.params?.hours ?? 0, param_days: rule.params?.days ?? 1,
    param_multiplier: m ? (m.fixed !== undefined ? m.fixed : m.default) : 1,
    param_weeks: 1, param_months_divisor: 1,
    fixed_amount: 0, exempt_override: null, annual_own_divisor_months: null, note: "",
  });
});

// ✅ 월 환산 근로시간 — 백엔드 calc.weekly_to_monthly_hours와 같은 규칙.
//
//   주휴 포함:   ROUNDUP(주당×4.345 + 주당×4.345÷5)
//   주휴 미포함: ROUNDUP(주당×4.345)
//
// 주휴 산입 판정은 "주휴를 뺀 월 환산이 70시간 이상"이다(사용자 확정, 2026-08-19).
// ⚠️ 4대보험 면제 판정(월 60시간)은 이 문턱과 다르다 — 60~70h 구간은 "보험 적용 +
//    주휴 미포함"이 되며 의도된 결정이다. 자세한 근거는 백엔드 calc.py 주석 참고.
// ⚠️ 중간 반올림 금지 — 올림은 마지막 한 번만(주40h → 208.56 → 209).
// ⚠️ 부동소수점으로 곱하면 43.449999…처럼 나와 경계값에서 올림이 어긋날 수 있어
//    정수 연산으로만 계산한다(일수·시간은 소수 2자리까지 → 정수배로 올려 처리).
const WEEKS_PER_MONTH_MILLI = 4345;          // 4.345 × 1000
const HOLIDAY_THRESHOLD_SCALED = 70 * 1e7;   // 월 70시간을 같은 배율로
const ceilDiv = (a, b) => Math.floor((a + b - 1) / b);
export function deriveHours(daysPerWeek, hoursPerDay) {
  const d = Math.round((Number(daysPerWeek) || 0) * 100);
  const h = Math.round((Number(hoursPerDay) || 0) * 100);
  if (d <= 0 || h <= 0) return null;
  const weeklyScaled = d * h;                                   // 주당 × 10^4
  const baseScaled = weeklyScaled * WEEKS_PER_MONTH_MILLI;      // 주당×4.345 × 10^7
  // 분모를 5×10^7로 통일해 정수 나눗셈 한 번으로 끝낸다(포함 6/5, 미포함 5/5).
  const numerator = baseScaled * (baseScaled >= HOLIDAY_THRESHOLD_SCALED ? 6 : 5);
  const monthly = ceilDiv(numerator, 5 * 1e7);
  return { weekly: weeklyScaled / 10000, monthly };
}

const won = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return Math.round(n).toLocaleString() + "원";
};

// ── 스타일(CLAUDE.md: 줄바꿈 금지, 자동생성 필드 회색 배경, 버튼 크기 명시) ──
const ROW_GRID = { display: "grid", gridTemplateColumns: "150px 1fr", gap: "8px 12px", alignItems: "center", marginBottom: 6, minHeight: 36 };
const LABEL = { fontWeight: 600, color: "#374151", fontSize: 13, border: "1px solid #d1d5db", borderRadius: 6, background: "#f9fafb", boxSizing: "border-box", height: 36, padding: "0 10px", display: "flex", alignItems: "center", justifyContent: "center", whiteSpace: "nowrap" };
const INPUT = { width: "100%", height: 36, border: "1px solid #d1d5db", borderRadius: 6, padding: "0 10px", fontSize: 13, boxSizing: "border-box" };
const HEADER_BTN = { width: "auto", flexShrink: 0, padding: "8px 18px", fontSize: 13, whiteSpace: "nowrap", color: "#222", background: "#fff", border: "1px solid #ccc", borderRadius: 6, cursor: "pointer", height: 36 };
const SAVE_BTN = { ...HEADER_BTN, color: "#fff", background: "#0b5fff", border: "none", fontWeight: 600 };
const BACK_BTN = { width: "auto", flexShrink: 0, padding: "0 10px", fontSize: 11, whiteSpace: "nowrap", color: "#6b7280", background: "#fff", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", height: 26 };

// ✅ 선택형 버튼(발주처 유형/근무지/분야 공용) — 미선택은 흰 배경, 선택은 파란색.
const CHOICE_BTN = (active) => ({
  height: 34, padding: "0 14px", fontSize: 13, whiteSpace: "nowrap", borderRadius: 6,
  border: active ? "1px solid #0b5fff" : "1px solid #d1d5db",
  background: active ? "#0b5fff" : "#fff", color: active ? "#fff" : "#374151",
  cursor: "pointer", fontWeight: active ? 600 : 400,
});
const BIG_CHOICE_BTN = (active) => ({ ...CHOICE_BTN(active), height: 46, padding: "0 22px", fontSize: 14 });

// ✅ Step1 — 발주처 유형 4버튼 → institution 최종 문자열 조립. 새 DB 컬럼/enum을
// 만들지 않고 institution(자유텍스트)에 최종 문자열만 저장한다(버튼은 입력 보조용).
const INSTITUTION_TYPES = ["금천구청", "시설관리공단", "문화재단", "기타"];
function buildInstitutionName(type, dept, other) {
  if (type === "금천구청") return dept.trim() ? `금천구청 ${dept.trim()}` : "금천구청";
  if (type === "시설관리공단") return "금천구시설관리공단";
  if (type === "문화재단") return "금천문화재단";
  if (type === "기타") return other.trim();
  return "";
}

// ✅ 계약 마스터(GisungContract.institution) → 발주처 4버튼 분류 — 완전일치 기준
// 확정(2026-08-01, "부서 완전일치 방식으로 전환" 승인 확정 규칙). startsWith류
// 접두 매칭을 전부 제거했다 — "금천구청소년상담복지센터"가 "금천구청"의 접두
// 문자열과 우연히 겹쳐 금천구청 부서로 오분류되던 문제(단어 경계 없는 접두
// 매칭) 재발 방지.
//   - "금천구청 OO과" 패턴(공백+"과"로 끝남)만 금천구청 그룹, dept=그 부서명.
//   - "금천구시설관리공단"과 완전히 같으면 시설관리공단.
//   - "재단법인 " 접두를 뗀 나머지가 "금천문화재단"과 같으면 문화재단(실제
//     계약 데이터가 전부 "재단법인 금천문화재단"으로 일관되게 입력돼 있어 이
//     접두만 정규화, 그 외 접두는 다루지 않음 — 확인된 범위만 처리).
//   - 그 외 전부(청소년상담복지센터, 청소년문화의집 등) → 기타, dept=원문
//     그대로(버튼 라벨 겸 견적서에 실릴 발주처 표기 그대로).
function classifyInstitution(raw) {
  const s = (raw || "").trim();
  const deptMatch = /^금천구청\s+(.+과)$/.exec(s);
  if (deptMatch) return { type: "금천구청", dept: deptMatch[1] };
  if (s === "금천구시설관리공단") return { type: "시설관리공단", dept: null };
  const normalized = s.replace(/^재단법인\s*/, "").trim();
  if (normalized === "금천문화재단") return { type: "문화재단", dept: null };
  return { type: "기타", dept: s };
}

// ✅ 근무지 버튼 기본값 제안 — 계약 마스터(GisungContract) 1건 → 짧은 근무지명.
// 우선순위: location(공단 계약 전용 필드)이 있으면 그대로, 없으면 service_name에서
// 연도 접두어("2026년 " 등)와 "(관리)용역" 접미어를 제거해 파생한다. 순수 함수로
// 분리 — 접미사 패턴이 늘어나도 이 함수 한 곳만 고치면 됨.
function deriveSiteName(contract) {
  if (!contract) return "";
  if (contract.location && contract.location.trim()) return contract.location.trim();
  let s = (contract.service_name || "").trim();
  s = s.replace(/^(\d{4}년도?\s*)/, "");
  s = s.replace(/\s*(관리\s*)?용역$/, "");
  s = s.trim();
  return s || (contract.service_name || "").trim();
}

let _groupKeySeq = 0;
const newGroupKey = () => `new-${Date.now()}-${_groupKeySeq++}`;

// ✅ [3단계] 조건 이름 자동 조립 — "야간 경비 주 5일"처럼 분야명 + 주 N일.
// 실양식(v3)의 열 머리글 표기 그대로. 직접 고치면(nameTouched) 더는 덮어쓰지 않는다.
const composeGroupName = (fieldName, daysPerWeek) => {
  const d = Number(daysPerWeek);
  return d > 0 ? `${fieldName} 주 ${d}일` : fieldName;
};

// ✅ 발주처별 기본 템플릿 공통 규칙
//   · 단가·횟수는 기본값일 뿐이고 팝업에서 수정 가능하다.
//   · 재료비는 amount가 곧 월액이다(amount×count÷divisor에서 count=divisor=계약개월수).
//   · 안전관리비는 수식형(rate)이라 amount/count를 쓰지 않는다(공단 비율식 기준).
// ⚠️ 발주처명 문자열 상수는 제거했다(2026-08-27) — 판정은 전부 template_key로
// 한다. 발주처 유형 버튼 → 템플릿 키 매핑이 유일한 판정 지점이다.
// 키가 없는 유형(금천구청·기타)은 템플릿·전용 기본값이 없다.
const TEMPLATE_KEY_BY_TYPE = { "시설관리공단": "gongdan", "문화재단": "munhwa" };
// ✅ 표지 수신처 조립 — 백엔드 xlsx_export.compose_recipient와 같은 규칙이다.
//   공단      → 근무지명 + 귀중 (본부 계약도 근무지가 "…공단 본부"라 그대로 맞는다)
//   금천구청  → "금천구청 " 접두를 뗀 과명 + 귀중
//   그 외     → 발주처명 그대로 + 귀중
// ⚠️ 접두 제거는 반드시 이 정규식으로 — 공백 + "…과"로 끝나는 경우만 잡는다.
// startsWith("금천구청")이면 실제 발주처인 "금천구청소년상담복지센터"가 걸려
// "소년상담복지센터 귀중"이 된다(classifyInstitution이 같은 사고로 접두 매칭을
// 걷어낸 이력이 있다).
// ⚠️ 한쪽만 고치면 저장값과 백엔드 폴백이 갈라진다. 두 곳을 함께 고칠 것.
const GUCHEONG_DEPT_RE = /^금천구청\s+(.+과)$/;

// ✅ 안전관리비 방식 — 발주처마다 다르다(사용자 확정 2026-08-21).
// 발주처 마스터 테이블이 없어 기본값은 이 매핑이 정하고, 실제 값은 견적서마다
// 저장된다. 매칭이 실패해도 per_person으로 떨어질 뿐이고 화면이 방식을 항상
// 보여주므로 조용히 틀리지 않는다.
// ⚠️ 매칭 규칙(공단 완전일치)은 composeRecipient와 같은 상수를 쓴다.
const SAFETY_METHODS = [
  { value: "rate", label: "비율식", hint: "(인건비+재료비)×요율, 연간 천원 올림" },
  { value: "per_person", label: "1인당 정액", hint: "단가 × 그 조건의 인원" },
  { value: "none", label: "미적용", hint: "안전관리비 항목을 두지 않음" },
];
// ✅ 안전관리비 기본 방식 — 발주처 템플릿 키가 정한다(이름 매칭 아님).
// 키가 없는 발주처는 per_person으로 떨어지고, 실제 값은 견적서마다 저장된다.
const SAFETY_METHOD_BY_TEMPLATE = { gongdan: "rate", munhwa: "per_person" };
const defaultSafetyMethod = (templateKey) =>
  SAFETY_METHOD_BY_TEMPLATE[templateKey] || "per_person";
// ⚠️ 백엔드 xlsx_export.compose_recipient와 같은 규칙 — 인자도 같다.
const composeRecipient = (institution, site, templateKey) => {
  const inst = (institution || "").trim();
  let target;
  if (templateKey === "gongdan") target = (site || "").trim() || inst;
  else { const m = GUCHEONG_DEPT_RE.exec(inst); target = m ? m[1] : inst; }
  return target ? `${target} 귀중` : "";
};
const SAFETY_RATE_DEFAULT = "2.07";
// ✅ 안전관리비 식별자 — 항목명이 아니라 이 값으로 판정한다(모델 item_kind와 같은 값).
// 이름 매칭은 항목명을 고치는 순간 새고, amount_mode("수식형")는 "금액을 어떻게
// 뽑는지"일 뿐이라 사용자가 만든 무관한 수식형 항목까지 안전관리비로 오인했다.
const SAFETY_KIND = "safety";
const isSafetyItem = (o) => (o || {}).item_kind === SAFETY_KIND;
// 안전관리비 비고(비율식) — 기존 문구 그대로 상수로 올린다.
const SAFETY_RATE_REMARK = "산업안전 및 중대재해 관련 안전관리비\n5억 원 이하: (인건비+재료비)*" + SAFETY_RATE_DEFAULT + "%";
// 문화재단 안전관리비 1인당 단가(사용자 확정 2026-08-27).
const MUNHWA_SAFETY_UNIT_PRICE = 12500;
// 발주처 제공 항목의 기본 비고 — 백엔드 models.PROVIDED_REMARK와 같은 문자열.
const PROVIDED_REMARK = "발주처 제공";

// ✅ 발주처별 기본 템플릿 — {template_key: (months) => [항목...]}.
// ⚠️ 백엔드 회귀 픽스처(quotation/tests.py의 _gongdan_items / _munhwa_items)와 같은
// 모양을 유지할 것. 한쪽만 고치면 "화면에선 맞는데 회귀는 옛 값을 얼려둔" 상태가 된다.
// ⚠️ 키가 없는 발주처(민간 등)는 여기 항목이 없다 = 빈 상태로 시작이 정상이다.
const QUOTATION_TEMPLATES = {
  // 금천구시설관리공단 — 안전관리비가 비율식(2.07%), 재료비는 우리가 댄다.
  gongdan: (m) => [
    { label: "피복비", block: "welfare", amount: 50000, count: 2, divisor_months: m,
      scales_with_personnel: true, note: "", remark: "반기별 작업복 지급",
      amount_mode: "fixed", rate: 0 },
    { label: "복리후생비(명절)", block: "welfare", amount: 50000, count: 2, divisor_months: m,
      scales_with_personnel: true, note: "", remark: "설날, 추석 명절 선물 지급",
      amount_mode: "fixed", rate: 0 },
    { label: "재료비", block: "etc", amount: 0, count: m, divisor_months: m,
      scales_with_personnel: false, note: "", remark: "",
      amount_mode: "fixed", rate: 0 },
    { label: "안전관리비", block: "etc", amount: 0, count: 1, divisor_months: m,
      scales_with_personnel: false, note: "", item_kind: SAFETY_KIND,
      remark: SAFETY_RATE_REMARK, amount_mode: "rate", rate: SAFETY_RATE_DEFAULT },
  ],
  // 금천문화재단 — 복리후생 2종은 공단과 같다. 다른 점 둘:
  //   · 재료비를 발주처가 현물로 댄다 → provided(금액 0, 계산에 0으로 참여, 행은 남는다)
  //   · 안전관리비가 1인당 정액 12,500원 (비율식 아님) — 방식 토글이 per_person이라
  //     금액은 상단 방식 설정이 정하고, 여기 amount/count는 그 단가·기간의 기본값이다.
  munhwa: (m) => [
    { label: "피복비", block: "welfare", amount: 50000, count: 2, divisor_months: m,
      scales_with_personnel: true, note: "", remark: "반기별 작업복 지급",
      amount_mode: "fixed", rate: 0 },
    { label: "복리후생비(명절)", block: "welfare", amount: 50000, count: 2, divisor_months: m,
      scales_with_personnel: true, note: "", remark: "설날, 추석 명절 선물 지급",
      amount_mode: "fixed", rate: 0 },
    { label: "재료비", block: "etc", amount: 0, count: m, divisor_months: m,
      scales_with_personnel: false, note: "", remark: PROVIDED_REMARK,
      amount_mode: "provided", rate: 0 },
    { label: "안전관리비", block: "etc", amount: MUNHWA_SAFETY_UNIT_PRICE, count: m, divisor_months: m,
      scales_with_personnel: true, note: "", item_kind: SAFETY_KIND,
      remark: "산업안전 및 중대재해 관련 안전관리비", amount_mode: "rate", rate: 0 },
  ],
};

const templateItems = (templateKey, months) => {
  const make = QUOTATION_TEMPLATES[templateKey];
  if (!make) return [];
  // 계약개월수를 아직 안 골랐으면 12로 — 팝업에서 개월수를 바꾸면 사용자가 고친다.
  const m = Number(months) > 0 ? Number(months) : 12;
  return make(m).map((o, i) => ({ ...o, id: `tpl-${Date.now()}-${i}` }));
};
/** 그 조건에 아직 없는 템플릿 항목만 — 같은 label이 있으면 건너뛴다(중복 방지). */
const missingTemplateItems = (group, templateKey, months) => {
  const have = new Set((group.other_items || []).map((o) => (o.label || "").trim()));
  return templateItems(templateKey, months).filter((o) => !have.has(o.label));
};

const makeGroupFromCategory = (cat, otherItems = []) => ({
  key: newGroupKey(), fieldName: cat.name, name: cat.name, nameTouched: false,
  personnel_count: 1, days_per_week: "", hours_per_day: "",
  weekly_hours: "", monthly_hours: "", hoursTouched: false,
  pension_health_care_exempt_override: "",
  allowance_items: defaultAllowanceItems(), other_items: otherItems,
});

// ✅ 신규 작성 진입 플로우 — 한 팝업 안에서 ①~⑤가 이어진다.
// 중간에 X로 닫으면 거기까지 고른 값만 반영하고 폼으로 들어간다(시퀀스만 중단).
// ⚠️ 스텝 수가 런타임에 변한다 — 조건(그룹)마다 2스텝이 붙기 때문이다.
// 그래서 배열 상수가 아니라 buildPopupOrder(groups, templateKey)로 매번 만든다.
const POPUP_ORDER_STATIC = [
  "year", "institution", "site", "field", "title",
  "months", "wage", "region", "rates", "fees",
];

// 조건 스텝 이름 — "grp:<그룹키>:<종류>". 그룹이 지워지면 그 이름은 order에서
// 자연히 빠지므로 별도 정리가 필요 없다.
const GRP = (key, kind) => `grp:${key}:${kind}`;
const parseGrp = (name) => {
  const m = /^grp:(.+):(basic|more|allow1|allow2|welfare|etc)$/.exec(name || "");
  return m ? { key: m[1], kind: m[2] } : null;
};

// 조건 1개 = 4스텝: 기본 → 제수당 전반 → 제수당 후반 → 조건 추가?
// (제수당을 연쇄 밖에 두었다가 다시 넣었다 — 사용자 확정 2026-08-20.
//  대신 대부분 0으로 통과하는 팝업이라 "없으면 그대로 확인" 안내를 붙인다.)
// ✅ 그 조건에 항목 스텝(복리후생·기타)을 붙일지 — 연쇄·구간막대·진행률이 모두
// 이 한 판정을 쓴다. 한 곳만 걸러내면 막대에는 칸이 남는데 팝업은 안 열려
// 진행률이 끝까지 안 차거나 [← 이전]이 열리지 않는 스텝으로 되돌아간다.
//
// 항목이 하나도 없고 공단도 아니면 건너뛴다(사용자 확정 2026-08-20) — 빈 팝업을
// 두 번 통과시키는 게 조건 수만큼 반복되기 때문. 공단이면 항목이 비어 있어도
// 남긴다: 그 팝업 안의 [공단 기본 4항목 채우기]가 채워 넣는 진입점이라서다.
// 템플릿이 있는 발주처면 항목이 비어 있어도 스텝을 남긴다 — 그 팝업 안의
// [기본 항목 채우기]가 채워 넣는 진입점이라서다.
const groupNeedsItemSteps = (g, templateKey) =>
  Boolean(templateKey) || (g.other_items || []).length > 0;

const buildPopupOrder = (groups, templateKey = false) => [
  ...POPUP_ORDER_STATIC,
  ...groups.flatMap((g) => [
    GRP(g.key, "basic"), GRP(g.key, "allow1"), GRP(g.key, "allow2"), GRP(g.key, "more"),
  ]),
  // ✅ Step 3(복리후생·기타)는 조건 연쇄가 전부 끝난 뒤 일괄로 온다(사용자 확정
  // 2026-08-20). 조건 중간에 끼우면 "조건 추가?"에서 조건이 늘어날 때마다 이미
  // 지나온 항목 스텝 뒤로 새 조건이 붙어 순서가 뒤엉킨다.
  ...groups.filter((g) => groupNeedsItemSteps(g, templateKey))
    .flatMap((g) => [GRP(g.key, "welfare"), GRP(g.key, "etc")]),
];
const POPUP_LABELS = {
  year: "연도", institution: "발주처", site: "근무지", field: "분야", title: "품명",
  months: "계약개월수", wage: "시급", region: "생활임금 지자체",
  rates: "4대보험 요율", fees: "일반관리비·이윤",
};
const GRP_KIND_LABEL = {
  basic: "조건", more: "조건 추가?", allow1: "제수당 전반", allow2: "제수당 후반",
  welfare: "복리후생", etc: "기타",
};
const popupLabel = (name, groups) => {
  const g = parseGrp(name);
  if (!g) return POPUP_LABELS[name] || name;
  const grp = groups.find((x) => x.key === g.key);
  const who = grp ? grp.name || grp.fieldName : "조건";
  return g.kind === "basic" ? `${who} 조건` : `${who} · ${GRP_KIND_LABEL[g.kind]}`;
};

// ✅ 2단 구간 진행 표시 — 칸 하나 = 구간 하나다(사용자 확정 2026-08-19).
// 스텝을 그대로 칸으로 그리면 조건 3개에서 22칸이 되어 실처럼 가늘어지고,
// 도중에 조건이 추가되면 총 수가 늘어 진행률이 역행한다. 구간 단위면 칸이
// 조건 수만큼만 늘고, 지난 구간은 채워진 채로 남는다.
const SEGMENTS_STATIC = [
  { label: "기본 정보", names: POPUP_ORDER_STATIC.slice(0, 5) },
  { label: "견적 조건", names: POPUP_ORDER_STATIC.slice(5) },
];
const buildSegments = (groups, templateKey = false) => [
  ...SEGMENTS_STATIC,
  ...groups.map((g) => ({
    label: g.name || g.fieldName || "조건",
    names: [GRP(g.key, "basic"), GRP(g.key, "allow1"), GRP(g.key, "allow2"), GRP(g.key, "more")],
  })),
  // 항목 스텝은 조건이 몇 개든 구간 하나로 묶는다 — 조건마다 구간을 또 만들면
  // 조건 3개에서 막대가 8칸이 되어 구간 단위로 바꾼 취지가 사라진다.
  ...(() => {
    const names = groups.filter((g) => groupNeedsItemSteps(g, templateKey))
      .flatMap((g) => [GRP(g.key, "welfare"), GRP(g.key, "etc")]);
    return names.length ? [{ label: "복리후생·기타", names }] : [];
  })(),
];
/** 현재 스텝의 구간 위치 — {segments, segIdx, inSeg, inSegCount} (없으면 null) */
const popupProgress = (name, groups, templateKey = false) => {
  const segments = buildSegments(groups, templateKey);
  const segIdx = segments.findIndex((sg) => sg.names.includes(name));
  if (segIdx < 0) return null;      // 제수당 팝업 등 구간 밖 — 막대를 안 그린다
  const seg = segments[segIdx];
  return {
    segments: segments.map((sg) => sg.label),
    segIdx,
    inSeg: seg.names.indexOf(name) + 1,
    inSegCount: seg.names.length,
    label: seg.label,
  };
};

// ⚠️ ⑥~⑩은 "비어 있음"으로 완료를 판정할 수 없다 — 계약개월 12, 지자체 "금천구",
// 관리비 5/8처럼 기본값이 있고 시급·요율은 진입 시 자동으로 채워진다. 값으로 보면
// 언제나 "채워짐"이라 팝업이 한 번도 뜨지 않는다. 그래서 이 다섯은 "사용자가
// 확정했는가"(confirmed)로 판정한다. ①~⑤의 값 기반 판정은 그대로 둔다.
const CONFIRM_STEPS = ["months", "wage", "region", "rates", "fees"];

// ⑧ 생활임금 지자체 3택. 값은 그대로 quotation.wage_region에 저장되고 출력에서
// "{연도}년 {값} 생활임금 ({시급}원)"으로 조립된다(calc.build_base_sheet /
// xlsx_export 표지·기준단가). 그래서 선택지 값이 곧 문구 조각이다.
const REGION_CHOICES = ["금천구", "서울시", "예상"];

export default function QuotationForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  // ✅ 3단계 — 1 기본 정보 / 2 근무조건·수당 / 3 복리후생·기타(저장·확정)
  // ⚠️ 화면 표시만 바꾼다. 폼 상태는 전부 이 컴포넌트에 있어 단계를 옮겨도 값이 남는다
  // (자식이 언마운트돼도 입력값은 여기 state에 있다).
  const [step, setStep] = useState(1);

  const [status, setStatus] = useState("draft");
  const [version, setVersion] = useState(null);
  const [institutionDisplay, setInstitutionDisplay] = useState("");

  // ── 기본정보 ──────────────────────────────────────
  const [year, setYear] = useState(currentYear + 1); // 기본값: 현재연도+1(=2027)
  const [institutionType, setInstitutionType] = useState("");
  // 편집 모드에서 서버가 준 케이스 템플릿 키(신규 작성에서는 빈 값)
  const [loadedTemplateKey, setLoadedTemplateKey] = useState("");
  const [institutionDept, setInstitutionDept] = useState("");   // 금천구청 전용
  const [institutionOther, setInstitutionOther] = useState(""); // 기타 전용
  const institutionName = useMemo(
    () => buildInstitutionName(institutionType, institutionDept, institutionOther),
    [institutionType, institutionDept, institutionOther]
  );
  // ✅ 템플릿이 있는 발주처면 조건마다 기본 4항목(복리후생 2 + 기타 2)을 자동
  // 생성한다. 발주처를 나중에 바꿔도 이미 만든 조건에는 소급하지 않는다
  // (사용자 확정 2026-08-20) — 사용자가 지운 항목을 되살리면 안 되기 때문.
  // 대신 조건 카드/기타 팝업의 [기본 항목 채우기] 버튼으로 그 조건에만 넣는다.
  // ✅ 발주처 템플릿 키 — 스텝1 버튼이 정한다. 이름 문자열을 런타임에 비교하지
  // 않는다(2026-08-27): 발주처명은 자유 텍스트라 "재단법인 금천문화재단"과
  // "금천문화재단"처럼 표기가 갈리고, 이름을 고치면 템플릿이 조용히 샌다.
  // 신규는 스텝1 버튼이, 편집은 저장된 케이스 값이 키를 준다.
  // (편집 모드에는 발주처 유형 버튼 단계가 없어 institutionType이 빈 값이다)
  const templateKey = TEMPLATE_KEY_BY_TYPE[institutionType] || loadedTemplateKey || "";

  const [contracts, setContracts] = useState([]);
  const [contractId, setContractId] = useState("");
  const [siteName, setSiteName] = useState("");
  const [title, setTitle] = useState("");
  const [titleTouched, setTitleTouched] = useState(false);
  // ✅ 중복 견적서(발주처+연도+품명) — 입력 중에 미리 알려주고, 확정/저장 때 막는다.
  // ⚠️ 판정은 서버가 한다(공백 정리 규칙이 한 곳에만 있어야 프론트·백엔드가 갈라지지
  // 않는다). 여기서는 물어보고 결과만 들고 있는다.
  const [dupCaseId, setDupCaseId] = useState(null);
  const [dupModal, setDupModal] = useState(false);
  const dupTimer = useRef(null);
  // 표지 수신처 — 규칙으로 자동 조립하되 사용자가 고칠 수 있다(고치면 recipientTouched).
  const [recipient, setRecipient] = useState("");
  const [recipientTouched, setRecipientTouched] = useState(false);
  const [safetyMethod, setSafetyMethod] = useState("per_person");
  const [safetyUnitPrice, setSafetyUnitPrice] = useState(0);
  const [safetyPeriod, setSafetyPeriod] = useState("monthly");
  const [safetyMethodTouched, setSafetyMethodTouched] = useState(false);
  const [safetyNoneConfirm, setSafetyNoneConfirm] = useState(null);   // 전환 대기 중인 값
  const [months, setMonths] = useState(12);

  const [wageRates, setWageRates] = useState([]);   // payroll WageRate (year, grade, hourly)
  // ✅ 연도별 4대보험 요율 묶음 — 기존 마스터(insurance.InsuranceRate)를 그대로 읽는다.
  // 새 테이블을 만들면 LivingWage처럼 또 갈라지므로 만들지 않는다.
  const [rateSets, setRateSets] = useState([]);
  const [rateApplied, setRateApplied] = useState("");   // 드롭다운에 표시할 선택 연도
  // ⑨ 팝업에서 요율 세트를 "고르려고 시도했는가" — 성공 여부와 무관하다.
  // applyRateSet이 실패하면 rateApplied가 안 채워져 5칸이 영영 안 나타나고,
  // 그러면 수동 입력조차 막힌다(2단 공개 도입 전에는 가능했다).
  const [ratePickTried, setRatePickTried] = useState(false);
  // 적용 당시 값 스냅샷 — 이후 개별 칸을 고치면 "(수정됨)"으로 바뀐다.
  const [rateSnapshot, setRateSnapshot] = useState(null);
  // ✅ 요율 출처 문구 — 선택 시점에 확정해 견적서에 저장한다(Quotation.rate_source_note).
  // 저장된 5개 요율 숫자로는 "준용이었는지"를 역추론할 수 없어서(고용보험은 합산값,
  // 산재는 사용자가 고침, 한 칸만 수정해도 어느 해와도 안 맞음) 문구를 그 자리에서
  // 굳혀 둔다. 비면 옛 견적서이고, 출력 쪽이 "{연도}년 요율"로 폴백한다.
  const [rateSourceNote, setRateSourceNote] = useState("");
  const [hourlyWage, setHourlyWage] = useState("");
  const [wageRegion, setWageRegion] = useState("금천구");
  const [pensionRate, setPensionRate] = useState("");
  const [healthRate, setHealthRate] = useState("");
  const [careRate, setCareRate] = useState("");
  const [employRate, setEmployRate] = useState("");
  const [injuryRate, setInjuryRate] = useState("");
  // ✅ [3단계] 일반관리비·이윤율은 예전에 calc.py에 5%/8%로 하드코딩돼 있었다.
  // 발주처·용역마다 다르므로(2027 구립도서관 야간 경비 실측 9%/10%) 입력받는다.
  const [generalAdminRate, setGeneralAdminRate] = useState("5");
  const [profitRate, setProfitRate] = useState("8");
  const [annualRounddown, setAnnualRounddown] = useState(true);

  // ── 분야/근무조건 그룹 ────────────────────────────
  const [fieldCategories, setFieldCategories] = useState([]);
  const [newFieldInput, setNewFieldInput] = useState("");
  const [addingField, setAddingField] = useState(false);
  const [groups, setGroups] = useState([]);

  // ── 팝업 시퀀스(①연도→②발주처→③근무지→④분야) ──────────
  const [activePopup, setActivePopup] = useState(null); // null|"year"|"institution"|"site"|"field"
  const [popupSeq, setPopupSeq] = useState(false); // true=자동 연쇄 중, false=[변경]으로 단독 재호출
  // ⚠️ "다음 팝업으로 이어가라"는 신호만 세우고 실제 이동은 effect가 한다 —
  // 확정 핸들러 안에서 missingPopupStep을 읽으면 방금 채운 값이 아직 반영되지 않은
  // 렌더의 값이라 방금 끝낸 스텝을 그대로 가리킨다(한 박자 느림). 값이 반영된 뒤
  // effect에서 읽어야 "이미 채운 스텝 건너뛰기"가 성립한다.
  const [chainPending, setChainPending] = useState(false);
  // ⑥~⑩의 완료 표시. 팝업 [확인]과 Step1에서 그 값을 직접 고친 순간(touched) 둘 다
  // 여기에 표시된다 — 후자가 없으면 폼에서 다 채워 놓고도 배너가 계속 남는다.
  const [confirmed, setConfirmed] = useState({});
  const [instSub, setInstSub] = useState("type"); // "type"|"dept"|"other"
  const [deptDirectInput, setDeptDirectInput] = useState(false);
  const [otherDirectInput, setOtherDirectInput] = useState(false);
  const [siteDirectInput, setSiteDirectInput] = useState(false);
  const [siteSearch, setSiteSearch] = useState("");

  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(!isEdit);
  const previewTimer = useRef(null);

  // ── 초기 로드 ──────────────────────────────────────────────
  useEffect(() => {
    api.get("gisung-gye/contracts/").then(({ data }) => {
      setContracts(Array.isArray(data) ? data : data.results || []);
    }).catch((e) => console.error("❌ 계약 목록 실패:", e));
    api.get("quotation/field-categories/").then(({ data }) => {
      setFieldCategories(Array.isArray(data) ? data : data.results || []);
    }).catch((e) => console.error("❌ 분야 목록 실패:", e));
    api.get("insurance/rates/").then(({ data }) => {
      setRateSets(Array.isArray(data) ? data : data.results || []);
    }).catch((e) => console.error("❌ 4대보험 요율 목록 실패:", e));
  }, []);

  // ✅ 시급 마스터는 급여 모듈의 "시급 설정"(payroll WageRate) 하나로 통합.
  // ⚠️ 기준 연도는 "오늘"이 아니라 "견적서의 연도"다 — 2027년 견적을 쓰면서 2026년
  // 시급만 보이면 안 된다. 연도를 바꾸면 다시 조회한다.
  // 해당 연도 등록값이 0건이면 전체 연도로 폴백한다(빈 드롭다운으로 두지 않는다).
  // 항목 표기에 연도가 들어 있어("2026년 생활임금") 섞여도 구분된다.
  useEffect(() => {
    if (!year) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("payroll/wage-rates/", { params: { year } });
        let rows = Array.isArray(data) ? data : data.results || [];
        if (rows.length === 0) {
          const all = await api.get("payroll/wage-rates/");
          rows = Array.isArray(all.data) ? all.data : all.data.results || [];
        }
        if (alive) setWageRates(rows);
      } catch (e) {
        console.error("❌ 시급등급 목록 실패:", e);
        if (alive) setWageRates([]);
      }
    })();
    return () => { alive = false; };
  }, [year]);

  // ✅ 신규 작성 진입 시 팝업 시퀀스 자동 시작(편집은 발주처가 이미 고정이라 시작 안 함).
  useEffect(() => {
    if (!isEdit) { setPopupSeq(true); setActivePopup("year"); }
  }, [isEdit]);

  // ✅ 시급 자동 채움 — 연도를 확정하면 그 연도 등록값이 조회되고, 도착하는 즉시 채운다.
  // ①에서 연도를 정한 뒤 ⑤ 품명까지 가는 동안 이미 채워져 있게 하려는 것.
  // 사용자가 직접 입력한 뒤에는 덮어쓰지 않는다(hourlyWage가 비어 있을 때만).
  useEffect(() => {
    if (isEdit || hourlyWage || wageRates.length === 0) return;
    const ofYear = wageRates.filter((w) => Number(w.year) === Number(year));
    const pool = ofYear.length ? ofYear : wageRates;
    const lw = pool.find((w) => (w.grade || "").includes("생활임금")) || pool[0];
    if (lw) setHourlyWage(String(lw.hourly));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wageRates, year, isEdit]);

  useEffect(() => {
    if (!isEdit) {
      // ✅ 신규 — 요율 자동 로드(최신 InsuranceRate + DailyWorkerRate 합산),
      // 산재요율은 마스터가 없어 "가장 최근에 쓴 값"을 기본값으로 제안(보완 4).
      (async () => {
        try {
          const { data: ins } = await api.get(`insurance/rates/for-year/?year=${currentYear}`);
          setPensionRate(String(ins.pension_rate));
          setHealthRate(String(ins.health_rate));
          setCareRate(String(ins.care_rate));
          let employ = Number(ins.employ_rate) || 0;
          try {
            const { data: daily } = await api.get(`payroll/daily-rates/?year=${currentYear}`);
            employ += Number(daily.employer_rate) || 0;
          } catch { /* 못 가져오면 InsuranceRate 값만 사용 */ }
          setEmployRate(String(employ));
        } catch (e) { console.error("❌ 요율 자동 로드 실패:", e); }
        try {
          const { data } = await api.get("quotation/versions/?ordering=-id");
          const list = Array.isArray(data) ? data : data.results || [];
          if (list[0]) setInjuryRate(String(list[0].injury_rate));
        } catch { /* 최초 견적서라면 비워둠 */ }
        // ⚠️ wage_region(지자체)은 grade 문자열로 채우지 않는다 — "2026년 생활임금"
        // 같은 등급명이 비고란에 "…년 2026년 생활임금 생활임금"으로 찍히기 때문.
        // 지자체는 폼에서 직접 입력하고 기본값 "금천구"를 유지한다.
        // 시급 자동 채움은 아래 별도 useEffect가 담당한다 — 이 시점엔 wageRates가
        // 아직 안 왔을 수 있어(연도 확정 → 조회 → 도착 순서) 여기서 채우면 비어버린다.
      })();
      return;
    }
    // ✅ 편집 — 기존 버전 불러오기(work_groups 포함)
    (async () => {
      try {
        const { data } = await api.get(`quotation/versions/${id}/`);
        setStatus(data.status); setVersion(data.version);
        setInstitutionDisplay(data.institution_display);
        setLoadedTemplateKey(data.case_template_key || "");
        setYear(data.year); setTitle(data.title); setTitleTouched(true);
        setMonths(data.months);
        setHourlyWage(String(data.hourly_wage)); setWageRegion(data.wage_region || "");
        setPensionRate(String(data.pension_rate)); setHealthRate(String(data.health_rate));
        setCareRate(String(data.care_rate)); setEmployRate(String(data.employ_rate));
        setInjuryRate(String(data.injury_rate));
        setRateSourceNote(data.rate_source_note || "");
        // 저장된 수신처가 있으면 사용자가 확정한 값으로 보고 자동 조립을 멈춘다.
        setRecipient(data.recipient || "");
        setRecipientTouched(Boolean(data.recipient));
        setSafetyMethod(data.safety_cost_method || "per_person");
        setSafetyUnitPrice(data.safety_unit_price ?? 0);
        setSafetyPeriod(data.safety_period || "monthly");
        setSafetyMethodTouched(true);
        setGeneralAdminRate(String(data.general_admin_rate));
        setProfitRate(String(data.profit_rate));
        setAnnualRounddown(data.annual_rounddown !== false);
        const loadedGroups = (data.work_groups || []).map((g) => ({
          key: g.id != null ? `db-${g.id}` : newGroupKey(),
          fieldName: g.name, name: g.name, nameTouched: true,
          personnel_count: g.personnel_count,
          // ✅ [3단계] 조건 3칸 — 3단계 이전 견적서는 null이라 빈칸으로 열린다.
          // 그 경우 weekly/monthly가 정답이므로 hoursTouched를 세워 보호한다.
          days_per_week: g.days_per_week == null ? "" : String(g.days_per_week),
          hours_per_day: g.hours_per_day == null ? "" : String(g.hours_per_day),
          hoursTouched: g.days_per_week == null || g.hours_per_day == null,
          weekly_hours: String(g.weekly_hours),
          monthly_hours: String(g.monthly_hours),
          pension_health_care_exempt_override:
            g.pension_health_care_exempt_override === null ? "" : String(g.pension_health_care_exempt_override),
          allowance_items: g.allowance_items?.length ? g.allowance_items : defaultAllowanceItems(),
          // ⚠️ 저장된 block은 이미 사용자가 고른(또는 확정된) 값이라 항목명을 고쳐도
          // 추천값이 덮어쓰면 안 된다 — 불러온 항목은 blockTouched로 잠근다.
          other_items: (g.other_items || []).map((o) => ({ ...o, blockTouched: true })),
        }));
        setGroups(loadedGroups);
      } catch (e) {
        console.error("❌ 견적서 불러오기 실패:", e);
        alert("견적서를 불러오지 못했습니다.");
      } finally {
        setLoaded(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ── 근무지 선택 시 제목 자동 조립 ────────────
  const pickContract = (c) => {
    setContractId(c.id);
    setSiteName(deriveSiteName(c)); // 팝업에서 버튼으로 고른 경우 항상 그 계약의 제안값으로 갱신
  };
  useEffect(() => {
    // ⚠️ 사용자가 폼에서 직접 고친 품명은 건드리지 않는다(기존 규칙 그대로).
    if (titleTouched) return;
    setTitle(siteName ? `${year}년 ${siteName} 용역` : "");
    // ✅ 자동값이 바뀌면 품명 확정을 푼다 — 한 번 확인해 둔 문구가 근무지·연도를
    // 고쳤다고 조용히 달라지면 안 된다. 연쇄가 새 자동값을 프리필한 채 다시 묻는다.
    setConfirmed((prev) => (prev.title ? { ...prev, title: false } : prev));
  }, [year, siteName, titleTouched]);

  // 발주처가 정해지면 안전관리비 방식 기본값을 채운다(직접 고른 뒤에는 덮지 않는다).
  useEffect(() => {
    if (isEdit || safetyMethodTouched) return;
    setSafetyMethod(defaultSafetyMethod(templateKey));
    // templateKey는 institutionType에서 파생되므로 institutionName과 함께 바뀐다 —
    // 의존성에 명시해 둔다(빠지면 유형만 바꾸고 이름이 같을 때 갱신을 놓친다).
  }, [isEdit, institutionName, templateKey, safetyMethodTouched]);

  // 발주처·근무지가 정해지면 수신처를 조립한다. 직접 고친 뒤에는 덮지 않는다.
  useEffect(() => {
    if (recipientTouched) return;
    setRecipient(composeRecipient(institutionName, siteName, templateKey));
  }, [institutionName, siteName, templateKey, recipientTouched]);

  // 품명을 치는 동안 디바운스로 중복 여부를 물어본다(편집 모드는 이미 있는 건이라 제외).
  useEffect(() => {
    if (isEdit) { setDupCaseId(null); return; }
    clearTimeout(dupTimer.current);
    if (!institutionName.trim() || !title.trim() || !year) { setDupCaseId(null); return; }
    dupTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("quotation/cases/check-duplicate/", {
          params: { institution: institutionName, year, title },
        });
        setDupCaseId(data.duplicate ? data.case_id : null);
      } catch { /* 조회 실패는 조용히 넘긴다 — 저장 시 서버가 한 번 더 막는다 */ }
    }, 400);
    return () => clearTimeout(dupTimer.current);
  }, [isEdit, institutionName, year, title]);

  const confirmStep = useCallback(
    (name) => setConfirmed((p) => (p[name] ? p : { ...p, [name]: true })), []);

  // Step1의 입력칸에 그대로 물리는 setter — 값 변경과 "확정했다" 표시를 같이 한다.
  // 팝업과 Step1이 같은 컨트롤 컴포넌트를 공유하므로 이 래퍼 하나만 내려주면
  // 어느 쪽에서 고쳐도 판정이 같아진다.
  const setMonthsC = (v) => { setMonths(v); confirmStep("months"); };
  const setHourlyWageC = (v) => { setHourlyWage(v); confirmStep("wage"); };
  const setWageRegionC = (v) => { setWageRegion(v); confirmStep("region"); };
  const setGeneralAdminRateC = (v) => { setGeneralAdminRate(v); confirmStep("fees"); };
  const setProfitRateC = (v) => { setProfitRate(v); confirmStep("fees"); };
  const applyRateSetC = (y) => { confirmStep("rates"); return applyRateSet(y); };
  const setPensionRateC = (v) => { setPensionRate(v); confirmStep("rates"); };
  const setHealthRateC = (v) => { setHealthRate(v); confirmStep("rates"); };
  const setCareRateC = (v) => { setCareRate(v); confirmStep("rates"); };
  const setEmployRateC = (v) => { setEmployRate(v); confirmStep("rates"); };
  const setInjuryRateC = (v) => { setInjuryRate(v); confirmStep("rates"); };

  // 조건 팝업에서 값을 고치면 그 조건의 ①이 확정된 것으로 본다(폼에서 고쳐도 동일).
  // ⚠️ 확정돼도 근무시간이 비어 있으면 missingPopupSteps가 계속 미완으로 잡는다.
  const setGroupFieldC = (key, field, value) => {
    setGroupField(key, field, value);
    confirmStep(GRP(key, "basic"));
  };
  // 열려 있는 조건 팝업이 가리키는 그룹
  const activeGrp = parseGrp(activePopup);
  const activeGroup = activeGrp ? groups.find((x) => x.key === activeGrp.key) : null;

  // ⑧ "예상" 선택 시 입력칸에 제시할 기본 금액 — 시급 마스터에 그 연도 "예상"
  // 등급이 등록돼 있으면 그 값을 쓴다(2027년 '예상 생활임금' 12,727원처럼).
  // 없으면 현재 시급을 그대로 제시한다. 연도를 하드코딩하지 않는다.
  const expectedHourly = useMemo(() => {
    const ofYear = wageRates.filter((w) => Number(w.year) === Number(year));
    const hit = ofYear.find((w) => (w.grade || "").includes("예상"));
    return hit ? String(hit.hourly) : "";
  }, [wageRates, year]);

  // ⑩ 일반관리비·이윤 제안값 — 2027년부터 9%/10%, 이전은 5%/8%.
  // ⚠️ 모델 기본값(5.0/8.0)은 건드리지 않는다. 저장된 견적서가 정본이라 편집 모드엔
  // 적용하지 않고, 사용자가 한 번이라도 확정하면 다시 덮어쓰지 않는다.
  useEffect(() => {
    if (isEdit || confirmed.fees) return;
    const [g, pr] = Number(year) >= 2027 ? ["9", "10"] : ["5", "8"];
    setGeneralAdminRate(g);
    setProfitRate(pr);
  }, [year, isEdit, confirmed.fees]);

  // ── 팝업 시퀀스 제어 ─────────────────────────────────────
  // ✅ [변경]으로 연 팝업도 확정하면 남은 미완 스텝으로 이어간다 — 닫았던 흐름을
  // 다시 붙이는 게 목적이라 진입 플로우인지 단독 호출인지를 구분하지 않는다.
  // 다만 전부 채워져 있으면 이어갈 곳이 없으므로 단독 호출로 둔다(진행 표시·이전 버튼 없음).
  const openPopup = (name) => {
    setPopupSeq(!!missingPopupStep);
    if (name === "institution") setInstSub("type");
    setActivePopup(name);
  };
  // ⚠️ 어디서 왔는지(from)로 다음을 정하지 않는다 — POPUP_ORDER 순번으로 넘기면
  // 이미 채운 스텝도 다시 연다. 다음 목적지는 항상 "남은 미완 스텝"이고, 그 계산은
  // 값이 반영된 뒤 effect가 한다(chainPending 주석 참고).
  const advancePopup = () => {
    setActivePopup(null);
    if (!popupSeq) return;
    setChainPending(true);
  };
  // 시퀀스 중 이전 단계로. 단독 호출([변경] 버튼)일 땐 뒤로 갈 곳이 없으니 그냥 닫는다.
  const backPopup = (from) => {
    if (!popupSeq) { setActivePopup(null); return; }
    const order = buildPopupOrder(groups, templateKey);
    const prev = order[order.indexOf(from) - 1];
    if (!prev) return;                       // 첫 단계에선 아무것도 안 함
    if (prev === "institution") setInstSub("type");
    setActivePopup(prev);
  };
  // ESC/배경클릭/X — 이전 선택은 유지하고 흐름만 멈춘다.
  // ⚠️ 닫기는 항상 "지금은 그만"으로 존중한다 — chainPending까지 내려서 effect가
  // 다음 팝업을 여는 일이 없게 한다. 이어가려면 [변경]이나 배너 버튼을 눌러야 한다.
  const closePopup = () => { setActivePopup(null); setPopupSeq(false); setChainPending(false); };

  // 팝업의 [← 이전] — 앞 스텝이 있을 때만 프롭을 준다(첫 스텝이면 버튼이 안 나온다).
  const backProps = (name) => {
    if (!popupSeq) return {};
    const order = buildPopupOrder(groups, templateKey);
    const prev = order[order.indexOf(name) - 1];
    return prev ? { onBack: () => backPopup(name), backLabel: popupLabel(prev, groups) } : {};
  };

  // ✅ 진입 플로우를 중간에 닫았을 때 다시 이어갈 지점.
  // ⚠️ 연도는 기본값이 늘 있어 "비어 있음"으로 판정되지 않으므로 재개 대상에서 빠진다
  // (연도를 고치려면 기존 [변경] 버튼을 쓰면 된다).
  // 편집 모드는 이미 다 채워진 견적서라 대상이 아니다.
  const missingPopupSteps = useMemo(() => {
    if (isEdit) return [];
    const out = [];
    if (!institutionName.trim()) out.push("institution");
    if (!siteName.trim()) out.push("site");
    if (groups.length === 0) out.push("field");
    // ✅ 품명은 값이 아니라 확인 여부로 판정한다(계약개월수 등 CONFIRM_STEPS와 동일).
    // 근무지에서 자동 조립되기 때문에 값 기반으로 보면 항상 채워져 있어 연쇄에서
    // 통째로 건너뛰었다 — 자동값이라도 눈으로 확인할 기회는 있어야 한다.
    // titleTouched(폼에서 직접 수정)도 확인으로 친다. 값이 비면 당연히 미완이다.
    if (!title.trim() || !(confirmed.title || titleTouched)) out.push("title");
    CONFIRM_STEPS.forEach((n) => { if (!confirmed[n]) out.push(n); });
    // 조건(그룹)마다 2스텝. 제수당은 여기 없다 — 연쇄 대상이 아니다.
    groups.forEach((g) => {
      // ⚠️ ① 기본은 값 기반 + confirmed 병행이다. 근무시간이 비어 있으면 확인을
      // 눌렀더라도 미완으로 둔다(validateStep2와 같은 필수값이라 안전장치).
      const hoursMissing = !g.days_per_week || !g.hours_per_day
        || !g.weekly_hours || !g.monthly_hours;
      if (hoursMissing || !confirmed[GRP(g.key, "basic")]) out.push(GRP(g.key, "basic"));
      // 제수당은 전부 기본값이 있어 값으로 판정할 수 없다 — confirmed만 본다.
      if (!confirmed[GRP(g.key, "allow1")]) out.push(GRP(g.key, "allow1"));
      if (!confirmed[GRP(g.key, "allow2")]) out.push(GRP(g.key, "allow2"));
      if (!confirmed[GRP(g.key, "more")]) out.push(GRP(g.key, "more"));
    });
    // ✅ Step 3 항목 스텝 — 조건 연쇄가 끝난 뒤 일괄. 항목은 비어 있는 게 정상인
    // 조건도 있어(공단이 아니면 템플릿이 안 깔린다) 값으로 판정하지 않고 confirmed만 본다.
    groups.filter((g) => groupNeedsItemSteps(g, templateKey)).forEach((g) => {
      if (!confirmed[GRP(g.key, "welfare")]) out.push(GRP(g.key, "welfare"));
      if (!confirmed[GRP(g.key, "etc")]) out.push(GRP(g.key, "etc"));
    });
    const order = buildPopupOrder(groups, templateKey);
    // 스텝 순서로 정렬 — 첫 원소가 "이어서 설정하기"의 시작점이 된다.
    return out.sort((a, b) => order.indexOf(a) - order.indexOf(b));
  }, [isEdit, institutionName, siteName, groups, title, titleTouched, confirmed, templateKey]);
  const missingPopupStep = missingPopupSteps[0] || null;

  // 확정 → (값 반영된 뒤) 남은 미완 스텝으로. 남은 게 없으면 조용히 끝난다.
  useEffect(() => {
    if (!chainPending) return;
    setChainPending(false);
    if (!missingPopupStep) { setPopupSeq(false); return; }
    if (missingPopupStep === "institution") setInstSub("type");
    setActivePopup(missingPopupStep);
  }, [chainPending, missingPopupStep]);

  const resumePopupSequence = () => {
    if (!missingPopupStep) return;
    setPopupSeq(true);                                  // 진행 표시·이전 버튼이 다시 살아난다
    if (missingPopupStep === "institution") setInstSub("type");
    setActivePopup(missingPopupStep);
  };

  const chooseInstitutionType = (t) => {
    setInstitutionType(t);
    if (t === "금천구청") { setInstSub("dept"); return; }
    if (t === "기타") { setInstSub("other"); return; }
    advancePopup(); // 시설관리공단/문화재단 — 추가 입력 없이 바로 다음
  };
  const pickContractAndAdvance = (c) => { pickContract(c); advancePopup(); };

  const deptOptions = useMemo(
    () => [...new Set(
      contracts.map((c) => classifyInstitution(c.institution)).filter((cls) => cls.type === "금천구청").map((cls) => cls.dept)
    )].filter(Boolean),
    [contracts]
  );
  // ✅ "기타" 버튼 — 4버튼 어디에도 안 걸리는 계약(청소년상담복지센터 등)을
  // 원문 그대로 버튼 라벨로 노출(department명 그대로 = 견적서 발주처 표기 그대로).
  const otherOptions = useMemo(
    () => [...new Set(
      contracts.map((c) => classifyInstitution(c.institution)).filter((cls) => cls.type === "기타").map((cls) => cls.dept)
    )].filter(Boolean),
    [contracts]
  );

  // ── 분야 선택/해제, 직접입력 등록 ───────────────────
  const toggleField = (cat) => {
    setGroups((prev) => {
      const exists = prev.some((g) => g.fieldName === cat.name);
      if (exists) return prev.filter((g) => g.fieldName !== cat.name);
      return [...prev, makeGroupFromCategory(cat, templateItems(templateKey, months))];
    });
  };
  const addNewFieldCategory = async () => {
    const name = newFieldInput.trim();
    if (!name) return;
    setAddingField(true);
    try {
      const { data } = await api.post("quotation/field-categories/", { name });
      setFieldCategories((prev) => (prev.some((c) => c.id === data.id) ? prev : [...prev, data]));
      setNewFieldInput("");
      setGroups((prev) => (prev.some((g) => g.fieldName === data.name)
        ? prev
        : [...prev, makeGroupFromCategory(data, templateItems(templateKey, months))]));
    } catch (e) {
      alert("분야 등록 실패: " + (e?.response?.data?.detail || e.message));
    } finally {
      setAddingField(false);
    }
  };
  // ⚠️ 그룹을 지우면 그 그룹의 confirmed 키도 같이 지운다. missingPopupSteps가
  // groups를 순회하므로 남겨둬도 연쇄·배너에 유령으로 뜨지는 않지만, 같은 key가
  // 재사용되면(복제 key 생성 규칙이 바뀌는 경우) 확정 상태가 되살아난다.
  const removeGroup = (key) => {
    setGroups((prev) => prev.filter((g) => g.key !== key));
    setConfirmed((prev) => {
      const next = {};
      Object.keys(prev).forEach((k) => {
        const parsed = parseGrp(k);
        if (!parsed || parsed.key !== key) next[k] = prev[k];
      });
      return next;
    });
  };

  // ✅ [3단계] "엑셀에서 열 하나 늘리듯" — 같은 분야에 조건 행 하나 추가.
  // 제수당·기타 항목 설정은 그대로 복사하고(조건마다 다시 입력하지 않게), 실제로
  // 달라지는 [인원수][주 N일][1일 N시간]만 비워서 바로 뒤에 끼워 넣는다.
  const duplicateGroup = (key) =>
    setGroups((prev) => {
      const i = prev.findIndex((g) => g.key === key);
      if (i < 0) return prev;
      const src = prev[i];
      const clone = {
        ...src, key: newGroupKey(), nameTouched: false,
        name: composeGroupName(src.fieldName, ""),
        days_per_week: "", hours_per_day: "",
        weekly_hours: "", monthly_hours: "", hoursTouched: false,
        allowance_items: src.allowance_items.map((a) => ({ ...a })),
        other_items: src.other_items.map((o, oi) => ({ ...o, id: `new-${Date.now()}-${oi}` })),
      };
      return [...prev.slice(0, i + 1), clone, ...prev.slice(i + 1)];
    });

  // ✅ 연도별 요율 묶음을 5칸에 일괄 적용.
  // ⚠️ 고용보험율 = InsuranceRate.employ_rate(근로자 부담분) + DailyWorkerRate.employer_rate
  // (고안직능 사업주율). 신규 작성 자동 채움이 원래 쓰던 계산과 같은 식이다.
  // ⚠️ 산재는 InsuranceRate.injury_rate가 등록돼 있을 때만 채운다. null(미등록)이면
  // 산재 칸을 건드리지 않고 현행(가장 최근 견적서 값 제안 + 직접 입력)을 유지한다 —
  // 사업장·업종마다 다른 값이라 0으로 덮어쓰면 안 된다.
  const applyRateSet = async (targetYear) => {
    if (!targetYear) return;
    try {
      const { data: ins } = await api.get("insurance/rates/for-year/", { params: { year: targetYear } });
      setPensionRate(String(ins.pension_rate));
      setHealthRate(String(ins.health_rate));
      setCareRate(String(ins.care_rate));
      let employ = Number(ins.employ_rate) || 0;
      try {
        const { data: daily } = await api.get("payroll/daily-rates/", { params: { year: targetYear } });
        employ += Number(daily.employer_rate) || 0;
      } catch { /* 못 가져오면 InsuranceRate 값만 사용 */ }
      const employStr = String(Number(employ.toFixed(4)));
      setEmployRate(employStr);
      const injuryStr = ins.injury_rate == null ? null : String(ins.injury_rate);
      if (injuryStr !== null) setInjuryRate(injuryStr);
      setRateApplied(String(targetYear));
      // 요청 연도와 실제 적용 연도가 다르면 준용이다 — 그 사실을 문구로 굳힌다.
      // 표기는 연도 2자리 축약(사용자 확정 2026-08-20):
      //   같으면      "27년 요율"
      //   준용이면    "26년 요율 (27년 요율 미정)"
      // ⚠️ 백엔드 xlsx_export._base_rate_note가 이 문구를 그대로 시트에 쓴다.
      // 형식을 바꾸면 그쪽 옛 형식 변환 규칙(_LEGACY_RATE_NOTE)도 함께 봐야 한다.
      const yy = (y) => String(y).slice(-2);
      const matched = String(ins.matched_year ?? targetYear);
      setRateSourceNote(matched === String(targetYear)
        ? `${yy(matched)}년 요율`
        : `${yy(matched)}년 요율 (${yy(targetYear)}년 요율 미정)`);
      setRateSnapshot({
        pension: String(ins.pension_rate), health: String(ins.health_rate),
        care: String(ins.care_rate), employ: employStr, injury: injuryStr,
        matchedYear: String(ins.matched_year ?? targetYear),
      });
    } catch (e) {
      alert("요율을 불러오지 못했습니다: " + (e?.response?.data?.detail || e.message));
    }
  };

  // 적용한 요율에서 개별 칸을 고쳤는가 — 드롭다운 표기를 "(수정됨)"으로 바꾸는 기준.
  // ⚠️ 산재는 그 연도에 등록돼 있어 실제로 채웠을 때만 비교한다. 미등록이면 애초에
  // 우리가 채우지 않았으므로 사용자가 뭘 넣든 "요율을 고쳤다"고 보지 않는다.
  // ✅ ⑨ 요율 팝업의 2단 공개 판정 — 별도 state를 두지 않고 파생한다.
  //   · 편집 모드: 이미 값이 있는 견적서라 처음부터 공개
  //   · confirmed["rates"]: 연쇄에서 한 번 확인한 뒤 [변경]으로 재진입한 경우
  //   · rateApplied !== "": 팝업 안에서 방금 세트를 고른 순간(applyRateSet이 채운다)
  // 아무것도 안 고르고 닫았다가 재개하면 다시 접힌 상태로 돌아온다 — 의도된 동작.
  //   · ratePickTried: 세트를 골랐는데 조회가 실패한 경우에도 수동 입력은 열어 둔다
  const ratesRevealed = isEdit || Boolean(confirmed.rates)
    || String(rateApplied || "") !== "" || ratePickTried;

  const rateModified = useMemo(() => {
    if (!rateSnapshot) return false;
    if (String(pensionRate) !== rateSnapshot.pension) return true;
    if (String(healthRate) !== rateSnapshot.health) return true;
    if (String(careRate) !== rateSnapshot.care) return true;
    if (String(employRate) !== rateSnapshot.employ) return true;
    if (rateSnapshot.injury !== null && String(injuryRate) !== rateSnapshot.injury) return true;
    return false;
  }, [rateSnapshot, pensionRate, healthRate, careRate, employRate, injuryRate]);

  // 수동 보정된 주당/월환산을 3칸 입력 기준 자동값으로 되돌린다(한 번의 상태 갱신으로
  // — setField를 여러 번 부르면 weekly/monthly 갱신이 hoursTouched를 다시 세워 무효화된다).
  const restoreAutoHours = (key) =>
    setGroups((prev) => prev.map((g) => {
      if (g.key !== key) return g;
      const d = deriveHours(g.days_per_week, g.hours_per_day);
      if (!d) return g;
      return { ...g, hoursTouched: false, weekly_hours: String(d.weekly), monthly_hours: String(d.monthly) };
    }));

  // ✅ [3단계] 조건 3칸 입력 → 주당/월환산 자동 채움 + 이름 자동 조립.
  // 주당/월환산을 직접 고치면 hoursTouched가 서서 더는 덮어쓰지 않는다(3단계 이전
  // 견적서·주휴 포함 209시간 같은 수동 보정을 보호 — "역산 안 함" 원칙과 같은 정신).
  const setGroupField = (key, field, value) =>
    setGroups((prev) => prev.map((g) => {
      if (g.key !== key) return g;
      const next = { ...g, [field]: value };
      if (field === "name") next.nameTouched = true;
      if (field === "weekly_hours" || field === "monthly_hours") next.hoursTouched = true;
      if (field === "days_per_week" || field === "hours_per_day") {
        const derived = deriveHours(next.days_per_week, next.hours_per_day);
        if (derived && !next.hoursTouched) {
          next.weekly_hours = String(derived.weekly);
          next.monthly_hours = String(derived.monthly);
        }
        if (!next.nameTouched) next.name = composeGroupName(next.fieldName, next.days_per_week);
      }
      return next;
    }));
  const setGroupAllowanceField = (key, idx, field, value) =>
    setGroups((prev) => prev.map((g) => (g.key !== key ? g : {
      ...g, allowance_items: g.allowance_items.map((a, i) => (i === idx ? { ...a, [field]: value } : a)),
    })));
  // 폼에서 제수당을 고치면 그 팝업이 확정된 것으로 본다(앞 3항목=전반, 뒤 3=후반).
  const setGroupAllowanceFieldC = (key, idx, field, value) => {
    setGroupAllowanceField(key, idx, field, value);
    confirmStep(GRP(key, idx < 3 ? "allow1" : "allow2"));
  };
  // ✅ [공단 기본 4항목 채우기] — 그 조건에만 주입한다. 같은 label이 이미 있으면
  // 건너뛰므로(missingGongdanItems) 두 번 눌러도 중복되지 않고, 사용자가 고쳐 둔
  // 금액도 덮어쓰지 않는다.
  const fillGongdanTemplate = (key) =>
    setGroups((prev) => prev.map((g) => {
      if (g.key !== key) return g;
      const add = missingTemplateItems(g, templateKey, months);
      return add.length ? { ...g, other_items: [...g.other_items, ...add] } : g;
    }));

  // ✅ 안전관리비 방식 변경. none으로 바꾸면 항목을 실제로 지운다 —
  // "항목이 있으면 계산에 포함된다"가 원칙이라 계산에서만 빼는 유령 상태를 만들지
  // 않는다(사용자 확정 2026-08-21). 되돌릴 수 없으니 확인을 받는다.
  const applySafetyMethod = (next) => {
    setSafetyMethodTouched(true);
    if (next !== "none") { setSafetyMethod(next); return; }
    const hasItem = groups.some((g) => (g.other_items || []).some(isSafetyItem));
    if (!hasItem) { setSafetyMethod(next); return; }
    setSafetyNoneConfirm(next);
  };
  const confirmSafetyNone = () => {
    setGroups((prev) => prev.map((g) => ({
      // ⚠️ amount_mode !== "rate"로 거르면 사용자가 만든 무관한 수식형 항목까지
      // 함께 지워진다(2026-08-21 실측). 안전관리비만 지운다.
      ...g, other_items: (g.other_items || []).filter((o) => !isSafetyItem(o)),
    })));
    setSafetyMethod("none");
    setSafetyNoneConfirm(null);
  };

  const addGroupOtherItem = (key) =>
    setGroups((prev) => prev.map((g) => (g.key !== key ? g : {
      ...g,
      other_items: [...g.other_items, {
        id: `new-${Date.now()}`, label: "", block: "etc", amount: 0, count: 1, divisor_months: 12,
        scales_with_personnel: true, note: "", remark: "", amount_mode: "fixed", rate: 0,
        item_kind: "",
      }],
    })));
  const removeGroupOtherItem = (key, idx) =>
    setGroups((prev) => prev.map((g) => (g.key !== key ? g : { ...g, other_items: g.other_items.filter((_, i) => i !== idx) })));
  // ⚠️ 항목명을 치면 블록을 추천값으로 따라 바꾸되, 사용자가 블록을 한 번이라도
  // 직접 고르면(blockTouched) 다시는 건드리지 않는다 — 라벨이 사용자의 선택을
  // 덮어쓰는 게 블록을 필드로 옮긴 이유 그 자체다. blockTouched는 화면 전용이라
  // buildPayload가 보내지 않는다(필드를 하나씩 골라 담는 구조라 자동으로 빠진다).
  const setGroupOtherField = (key, idx, field, value) =>
    setGroups((prev) => prev.map((g) => (g.key !== key ? g : {
      ...g,
      other_items: g.other_items.map((o, i) => {
        if (i !== idx) return o;
        const next = { ...o, [field]: value };
        if (field === "block") next.blockTouched = true;
        if (field === "label" && !o.blockTouched) next.block = suggestBlock(value);
        return next;
      }),
    })));

  // ── 파생 payload(저장/미리보기 공용) ──────────────────────
  const buildPayload = () => ({
    year: Number(year), title, months: Number(months),
    hourly_wage: Number(hourlyWage) || 0, wage_region: wageRegion,
    pension_rate: Number(pensionRate) || 0, health_rate: Number(healthRate) || 0,
    care_rate: Number(careRate) || 0, employ_rate: Number(employRate) || 0, injury_rate: Number(injuryRate) || 0,
    rate_source_note: rateSourceNote,
    recipient: recipient,
    safety_cost_method: safetyMethod,
    safety_unit_price: Number(safetyUnitPrice) || 0,
    safety_period: safetyPeriod,
    general_admin_rate: Number(generalAdminRate) || 0, profit_rate: Number(profitRate) || 0,
    annual_rounddown: annualRounddown,
    work_groups: groups.map((g, i) => ({
      name: g.name, personnel_count: Number(g.personnel_count) || 1,
      // ✅ [3단계] 조건 3칸 — 비어 있으면 null(3단계 이전 견적서 그대로 저장).
      days_per_week: g.days_per_week === "" ? null : Number(g.days_per_week),
      hours_per_day: g.hours_per_day === "" ? null : Number(g.hours_per_day),
      weekly_hours: Number(g.weekly_hours) || 0, monthly_hours: Number(g.monthly_hours) || 0,
      pension_health_care_exempt_override:
        g.pension_health_care_exempt_override === "" ? null : g.pension_health_care_exempt_override === "true",
      order: i,
      // ✅ 고정 규칙대로 정정해서 보낸다 — 옛 데이터도 재저장하면 규칙에 맞춰진다.
      allowance_items: g.allowance_items.map(normalizeAllowance).map(({ category, input_mode, param_hours, param_days, param_multiplier, param_weeks, fixed_amount, exempt_override, annual_own_divisor_months, note }) => ({
        category, input_mode, param_hours, param_days, param_multiplier,
        param_weeks: param_weeks == null ? 1 : roundWeeks(param_weeks),
        // ⚠️ 제수당은 매달 발생 → 항상 ÷1. 계약개월수를 넣으면 월액이 그만큼 축소된다.
        // 옛 값이 남아 있는 견적서를 다시 저장할 때도 여기서 1로 바로잡힌다.
        param_months_divisor: 1,
        fixed_amount, exempt_override, annual_own_divisor_months, note,
      })),
      other_items: g.other_items.map(({ label, block, amount, count, divisor_months, scales_with_personnel, note, remark, amount_mode, rate, item_kind, order }) => ({
        label, block: block || suggestBlock(label),
        amount: Number(amount) || 0, count: Number(count) || 1, divisor_months: Number(divisor_months) || 12,
        // ⚠️ 수식형은 밑수가 이미 월간(인원 반영) 기준이라 인원을 또 곱하면 이중 계상이다 —
        // 백엔드 calc도 저장값과 무관하게 False로 강제하지만, 보내는 값부터 맞춰 둔다.
        // ⚠️ 수식형은 밑수가 이미 월간(인원 반영)이라 인원을 또 곱하면 이중 계상이고,
        // 발주처 제공은 금액이 0이라 인원 곱셈 자체가 의미가 없다(문구에도 "*N명"이
        // 붙으면 안 된다) — 둘 다 false로 고정한다.
        scales_with_personnel: (amount_mode === "rate" || amount_mode === "provided")
          ? false : scales_with_personnel !== false,
        note, remark: remark || "",
        amount_mode: amount_mode || "fixed", rate: Number(rate) || 0,
        item_kind: item_kind || "",
        order: order || 0,
      })),
    })),
  });

  // ── 실시간 미리보기(디바운스) — 전 단계 공통 유지 ────────────
  useEffect(() => {
    if (!loaded) return;
    const ready = hourlyWage && groups.length > 0 && groups.every((g) => g.weekly_hours && g.monthly_hours);
    if (!ready) { setPreview(null); return; }
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.post("quotation/versions/preview-unsaved/", buildPayload());
        setPreview(data);
      } catch (e) {
        console.error("❌ 미리보기 계산 실패:", e?.response?.data || e);
      }
    }, 400);
    return () => clearTimeout(previewTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, year, title, months, hourlyWage, wageRegion, pensionRate, healthRate, careRate, employRate, injuryRate,
      generalAdminRate, profitRate, annualRounddown, groups]);

  // ── 저장/확정/새버전/엑셀 ──────────────────────────────────
  const handleSave = async () => {
    if (!title.trim()) return alert("품명을 입력해주세요.");
    if (!isEdit && !institutionName.trim() && !contractId) return alert("발주처를 선택하거나 입력해주세요.");
    if (groups.length === 0) return alert("근무조건 그룹을 최소 1개 이상 추가해주세요.");
    setSaving(true);
    try {
      if (isEdit) {
        const { data } = await api.patch(`quotation/versions/${id}/`, buildPayload());
        if (data._auto_new_version) {
          alert(data._message);
          navigate(`/quotation/${data.id}/edit`);
          return;
        }
        alert("저장했습니다.");
      } else {
        // ⚠️ year는 중복 판정용으로만 보낸다(QuotationCase의 필드가 아니다).
        const { data: caseData } = await api.post("quotation/cases/", {
          contract: contractId || null,
          institution_name: institutionName, service_name: title, year: Number(year),
          // ✅ 발주처 템플릿 키를 케이스에 저장한다 — 이후 판정(템플릿·안전관리비
          // 기본 방식·표지 수신처)은 전부 이 값으로 하고 발주처명은 안 본다.
          template_key: templateKey,
        });
        const { data } = await api.post("quotation/versions/", { case: caseData.id, ...buildPayload() });
        alert("저장했습니다.");
        navigate(`/quotation/${data.id}/edit`);
      }
    } catch (e) {
      // 서버가 중복을 막은 경우 — 같은 안내 모달로 연결한다(화면 차단이 뚫린 경로).
      const dup = e?.response?.status === 400 && e?.response?.data?.case_id;
      if (dup) { setDupCaseId(e.response.data.case_id); setDupModal(true); return; }
      alert("저장 실패: " + JSON.stringify(e?.response?.data || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async () => {
    if (!isEdit) return alert("먼저 저장한 뒤 확정할 수 있습니다.");
    if (!window.confirm("확정하면 이 버전은 더 이상 수정할 수 없습니다(수정하려면 새 버전이 만들어집니다). 계속할까요?")) return;
    setSaving(true);
    try {
      await api.post(`quotation/versions/${id}/finalize/`);
      setStatus("final");
      alert("확정했습니다.");
    } catch (e) {
      alert("확정 실패: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleNewVersion = async () => {
    if (!isEdit) return;
    setSaving(true);
    try {
      const { data } = await api.post(`quotation/versions/${id}/new_version/`);
      navigate(`/quotation/${data.id}/edit`);
    } catch (e) {
      alert("새 버전 생성 실패: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  // ✅ 목록 화면과 같은 방식 — JWT 전용 인증이라 window.open으로는 토큰이 안 실려 401.
  const handleExport = async () => {
    if (!isEdit) return alert("먼저 저장한 뒤 다운로드할 수 있습니다.");
    setSaving(true);
    try {
      await downloadFileWithAlert(
        `quotation/versions/${id}/export-xlsx/`,
        // ⚠️ fallback일 뿐이다 — 실제 이름은 서버가 준다(목록 화면과 같은 값).
        "견적서.xlsx",
        "견적서 다운로드에 실패했습니다."
      );
    } finally {
      setSaving(false);
    }
  };

  if (isEdit && !loaded) return <div style={{ padding: 16 }}>불러오는 중...</div>;
  const readOnly = status === "final";

  // ── 단계 이동 유효성 — 그 단계에서 확인 가능한 것만 본다(차단 아님/알림) ──
  const validateStep1 = () => {
    if (!year) return "연도를 입력해주세요.";
    if (!isEdit && !institutionType) return "발주처를 선택해주세요.";
    if (!isEdit && institutionType === "금천구청" && !institutionDept.trim()) return "발주 부서를 입력해주세요.";
    if (!isEdit && institutionType === "기타" && !institutionOther.trim()) return "기관명을 입력해주세요.";
    if (!title.trim()) return "품명을 입력해주세요(근무지를 고르면 자동 조립됩니다).";
    if (!hourlyWage) return "시급을 입력해주세요.";
    return null;
  };
  const validateStep2 = () => {
    if (groups.length === 0) return "분야를 최소 1개 이상 선택해주세요.";
    const missing = groups.find((g) => !g.weekly_hours || !g.monthly_hours);
    if (missing) return `"${missing.name}" 조건의 주당/월 근로시간을 입력해주세요.`;
    return null;
  };

  // ✅ 편집 모드는 아무 단계나 바로 이동 가능(이미 다 채워진 견적서라서).
  // 신규 작성은 순서대로 — 앞 단계 필수값이 비면 그 단계에 붙잡아 둔다.
  const gotoStep = (next) => {
    if (next === step) return;
    if (isEdit || next < step) { setStep(next); return; }
    const err = (step === 1 ? validateStep1() : step === 2 ? validateStep2() : null);
    if (err) return alert(err);
    setStep(next);
  };

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
        <h2 style={{ margin: 0, whiteSpace: "nowrap" }}>
          용역 견적서 {isEdit ? `편집 (v${version})` : "작성"}
          {readOnly && <span style={{ marginLeft: 10, fontSize: 12, padding: "3px 10px", borderRadius: 12, background: "#d1fae5", color: "#065f46", whiteSpace: "nowrap" }}>확정됨 — 수정 시 새 버전 생성</span>}
        </h2>
        <StepIndicator step={step} gotoStep={gotoStep} />
      </div>

      <div style={{ display: "flex", gap: 16 }}>
        {/* ── 좌측: 입력 ─────────────────────────────── */}
        <div style={{ flex: "0 0 520px" }}>
          {step <= 3 && (
            <MainStep
              step={step}
              isEdit={isEdit} readOnly={readOnly}
              institutionDisplay={institutionDisplay} institutionName={institutionName}
              year={year} siteName={siteName} title={title}
              setTitle={(v) => { setTitle(v); setTitleTouched(true); }}
              months={months} setMonths={setMonthsC}
              wageRates={wageRates} hourlyWage={hourlyWage} setHourlyWage={setHourlyWageC}
              wageRegion={wageRegion} setWageRegion={setWageRegionC} expectedHourly={expectedHourly}
              pensionRate={pensionRate} setPensionRate={setPensionRateC}
              healthRate={healthRate} setHealthRate={setHealthRateC}
              careRate={careRate} setCareRate={setCareRateC}
              employRate={employRate} setEmployRate={setEmployRateC}
              injuryRate={injuryRate} setInjuryRate={setInjuryRateC}
              rateSets={rateSets} applyRateSet={applyRateSetC} rateApplied={rateApplied}
              rateModified={rateModified} rateSnapshot={rateSnapshot}
              generalAdminRate={generalAdminRate} setGeneralAdminRate={setGeneralAdminRateC}
              profitRate={profitRate} setProfitRate={setProfitRateC}
              annualRounddown={annualRounddown} setAnnualRounddown={setAnnualRounddown}
              missingPopupStep={missingPopupStep} missingPopupSteps={missingPopupSteps}
              resumePopupSequence={resumePopupSequence}
              groups={groups} openPopup={openPopup} duplicateGroup={duplicateGroup}
              gongdanFill={(g) => (templateKey && missingTemplateItems(g, templateKey, months).length > 0
                ? () => fillGongdanTemplate(g.key) : null)}
              safetyMethod={safetyMethod} applySafetyMethod={applySafetyMethod}
              safetyUnitPrice={safetyUnitPrice} setSafetyUnitPrice={setSafetyUnitPrice}
              safetyPeriod={safetyPeriod} setSafetyPeriod={setSafetyPeriod}
              restoreAutoHours={restoreAutoHours}
              removeGroup={removeGroup} setGroupField={setGroupFieldC}
              setGroupAllowanceField={setGroupAllowanceFieldC}
              addGroupOtherItem={addGroupOtherItem} removeGroupOtherItem={removeGroupOtherItem}
              setGroupOtherField={setGroupOtherField}
            />
          )}
          {step === 3 && (
            <Step2
              isEdit={isEdit} readOnly={readOnly} saving={saving}
              institutionDisplay={isEdit ? institutionDisplay : institutionName}
              year={year} title={title} months={months} siteName={siteName}
              groups={groups}
              recipient={recipient}
              setRecipient={(v) => { setRecipient(v); setRecipientTouched(true); }}
              onSave={handleSave} onFinalize={handleFinalize}
              onNewVersion={handleNewVersion} onExport={handleExport}
            />
          )}

          {/* 하단 이동 버튼 — 3단계에선 위 Step2의 저장/확정 버튼이 그 역할을 한다 */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16 }}>
            {step > 1 ? (
              <button type="button" style={HEADER_BTN} onClick={() => gotoStep(step - 1)}>← 이전</button>
            ) : <span />}
            {step < 3 ? (
              <button type="button" style={SAVE_BTN} onClick={() => gotoStep(step + 1)}>다음 →</button>
            ) : <span />}
          </div>
        </div>

        {/* ── 우측: 실시간 계산 미리보기 — 엑셀 시트 탭 방식 ────────── */}
        <div style={{ flex: 1, minWidth: 380 }}>
          <PreviewPanel
            preview={preview} months={months} title={title} year={year}
            institution={isEdit ? institutionDisplay : institutionName}
            hourlyWage={hourlyWage} wageRegion={wageRegion}
            pensionRate={pensionRate} healthRate={healthRate} careRate={careRate}
            employRate={employRate} injuryRate={injuryRate}
            generalAdminRate={generalAdminRate} profitRate={profitRate}
            annualRounddown={annualRounddown}
          />
        </div>
      </div>

      {activePopup === "year" && (
        <YearPopup year={year} setYear={setYear} onConfirm={() => advancePopup()} onClose={closePopup}
          progress={popupSeq ? popupProgress("year", groups, templateKey) : null} {...backProps("year")} />
      )}
      {activePopup === "institution" && (
        <InstitutionPopup
          instSub={instSub} setInstSub={setInstSub}
          institutionType={institutionType} chooseInstitutionType={chooseInstitutionType}
          institutionDept={institutionDept} setInstitutionDept={setInstitutionDept}
          deptOptions={deptOptions} deptDirectInput={deptDirectInput} setDeptDirectInput={setDeptDirectInput}
          institutionOther={institutionOther} setInstitutionOther={setInstitutionOther}
          otherOptions={otherOptions} otherDirectInput={otherDirectInput} setOtherDirectInput={setOtherDirectInput}
          onConfirmDept={() => advancePopup()}
          onConfirmOther={() => advancePopup()}
          onClose={closePopup}
          progress={popupSeq ? popupProgress("institution", groups, templateKey) : null} {...backProps("institution")}
        />
      )}
      {activePopup === "site" && (
        <SitePopup
          contracts={contracts}
          institutionType={institutionType} institutionDept={institutionDept} institutionOther={institutionOther}
          contractId={contractId}
          pickContractAndAdvance={pickContractAndAdvance}
          siteName={siteName} setSiteName={setSiteName}
          siteDirectInput={siteDirectInput} setSiteDirectInput={setSiteDirectInput}
          siteSearch={siteSearch} setSiteSearch={setSiteSearch}
          onConfirmDirect={() => advancePopup()}
          onClose={closePopup}
          progress={popupSeq ? popupProgress("site", groups, templateKey) : null} {...backProps("site")}
        />
      )}
      {activePopup === "field" && (
        <FieldPopup
          fieldCategories={fieldCategories} groups={groups} toggleField={toggleField}
          newFieldInput={newFieldInput} setNewFieldInput={setNewFieldInput}
          addNewFieldCategory={addNewFieldCategory} addingField={addingField}
          onDone={() => advancePopup()}
          onClose={closePopup}
          progress={popupSeq ? popupProgress("field", groups, templateKey) : null} {...backProps("field")}
        />
      )}
      {activePopup === "months" && (
        <StepPopup title="⑥ 계약개월수" onConfirm={() => { confirmStep("months"); advancePopup(); }}
          onClose={closePopup} progress={popupSeq ? popupProgress("months", groups, templateKey) : null} {...backProps("months")}
          hint="자주 쓰는 값은 버튼으로, 그 외는 직접 입력하세요(0.5개월 단위).">
          <MonthsField months={months} setMonths={setMonths} readOnly={false} />
        </StepPopup>
      )}
      {activePopup === "wage" && (
        <StepPopup title="⑦ 시급" onConfirm={() => { confirmStep("wage"); advancePopup(); }}
          onClose={closePopup} progress={popupSeq ? popupProgress("wage", groups, templateKey) : null} {...backProps("wage")}
          hint="시급 설정(급여 모듈)에 등록된 값입니다 — 목록에 없으면 직접 입력하세요.">
          <WageField wageRates={wageRates} hourlyWage={hourlyWage} setHourlyWage={setHourlyWage} readOnly={false} />
        </StepPopup>
      )}
      {activePopup === "region" && (
        <StepPopup title="⑧ 생활임금 지자체" onConfirm={() => { confirmStep("region"); advancePopup(); }}
          onClose={closePopup} progress={popupSeq ? popupProgress("region", groups, templateKey) : null} {...backProps("region")}
          hint={`견적서에 "${year}년 ${wageRegion || "○○"} 생활임금 (${Number(hourlyWage || 0).toLocaleString()}원)"으로 나갑니다.`}>
          <RegionField wageRegion={wageRegion} setWageRegion={setWageRegion}
            expectedHourly={expectedHourly} hourlyWage={hourlyWage} setHourlyWage={setHourlyWage}
            readOnly={false} />
        </StepPopup>
      )}
      {activePopup === "rates" && (
        <StepPopup title="⑨ 4대보험 요율" onConfirm={() => { confirmStep("rates"); advancePopup(); }}
          onClose={closePopup} progress={popupSeq ? popupProgress("rates", groups, templateKey) : null} {...backProps("rates")}
          hint={ratesRevealed
            ? "선택한 요율은 이 견적서에 복사됩니다 — 나중에 마스터가 바뀌어도 움직이지 않습니다."
            : null}>
          <RateSetField
            mode="popup" revealed={ratesRevealed}
            year={year} rateSets={rateSets} rateApplied={rateApplied}
            rateModified={rateModified} rateSnapshot={rateSnapshot}
            applyRateSet={(y) => { setRatePickTried(true); return applyRateSet(y); }}
            pensionRate={pensionRate} setPensionRate={setPensionRate}
            healthRate={healthRate} setHealthRate={setHealthRate}
            careRate={careRate} setCareRate={setCareRate}
            employRate={employRate} setEmployRate={setEmployRate}
            injuryRate={injuryRate} setInjuryRate={setInjuryRate}
            readOnly={false}
          />
        </StepPopup>
      )}
      {activePopup === "fees" && (
        <StepPopup title="⑩ 일반관리비·이윤" onConfirm={() => { confirmStep("fees"); advancePopup(); }}
          onClose={closePopup} progress={popupSeq ? popupProgress("fees", groups, templateKey) : null} {...backProps("fees")}
          hint={`${year}년 기준 제안값입니다 — 발주처·용역마다 다르므로 필요하면 고치세요.`}>
          <FeeField generalAdminRate={generalAdminRate} setGeneralAdminRate={setGeneralAdminRate}
            profitRate={profitRate} setProfitRate={setProfitRate} readOnly={false} />
        </StepPopup>
      )}
      {activeGrp && activeGroup && activeGrp.kind === "basic" && (
        <StepPopup
          title={`${activeGroup.name || activeGroup.fieldName} — 조건`}
          onConfirm={() => { confirmStep(activePopup); advancePopup(); }}
          onClose={closePopup}
          progress={popupSeq ? popupProgress(activePopup, groups, templateKey) : null}
          {...backProps(activePopup)}
          hint="인원·근무일수·1일 시간을 넣으면 주당/월 환산시간이 자동으로 채워집니다.">
          {/* 조건명 — 폼 카드와 달리 [삭제]는 두지 않는다(연쇄 중 오삭제 방지). */}
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 10 }}>
            <input value={activeGroup.name}
              onChange={(e) => setGroupFieldC(activeGroup.key, "name", e.target.value)}
              style={{ ...INPUT, flex: 1, fontWeight: 700 }} placeholder="조건명(주 N일을 넣으면 자동 조립)" />
            <button type="button" onClick={() => duplicateGroup(activeGroup.key)}
              title="같은 분야에 조건 추가(엑셀 열 하나 늘리듯)"
              style={{ width: 92, height: 34, flexShrink: 0, fontSize: 12, whiteSpace: "nowrap",
                border: "1px solid #93c5fd", borderRadius: 6, background: "#fff", color: "#1d4ed8", cursor: "pointer" }}>
              + 조건 추가
            </button>
          </div>
          <GroupBasicField
            g={activeGroup} readOnly={false}
            setField={(f, v) => setGroupFieldC(activeGroup.key, f, v)}
            onRestoreAuto={() => restoreAutoHours(activeGroup.key)}
          />
        </StepPopup>
      )}
      {activeGrp && activeGroup && (activeGrp.kind === "allow1" || activeGrp.kind === "allow2") && (
        <StepPopup
          title={`${activeGroup.name || activeGroup.fieldName} — ${activeGrp.kind === "allow1" ? "제수당 전반" : "제수당 후반"}`}
          onConfirm={() => { confirmStep(activePopup); advancePopup(); }}
          onClose={closePopup}
          progress={popupSeq ? popupProgress(activePopup, groups, templateKey) : null}
          {...backProps(activePopup)}
          hint={activeGrp.kind === "allow1" ? "연장·야간·휴일 — 다음은 반장·연차·교통입니다."
                                            : "반장·연차·교통 — 다음은 조건 추가 여부입니다."}>
          {/* ⚠️ 6항목 중 실제로 쓰는 건 보통 1~2개다. 0으로 통과하는 게 정상이라는
              것을 먼저 알려 머뭇거리지 않게 한다. */}
          <div style={{ fontSize: 12, color: "#1e40af", background: "#eff6ff",
            border: "1px solid #bfdbfe", borderRadius: 6, padding: "6px 10px", marginBottom: 10 }}>
            입력할 수당이 없으면 그대로 [확인]을 누르세요.
          </div>
          {activeGroup.allowance_items
            .map((a, idx) => ({ a, idx }))
            .filter(({ idx }) => (activeGrp.kind === "allow1" ? idx < 3 : idx >= 3))
            .map(({ a, idx }) => (
              <AllowanceItemField key={a.category} a={a} idx={idx} readOnly={false} months={months}
                setAllowanceField={(i, f, v) => setGroupAllowanceFieldC(activeGroup.key, i, f, v)}
                weeklyExempt={Number(activeGroup.weekly_hours) > 0 && Number(activeGroup.weekly_hours) < 15} />
            ))}
        </StepPopup>
      )}
      {activeGrp && activeGroup && activeGrp.kind === "more" && (
        <StepPopup
          title={`${activeGroup.name || activeGroup.fieldName} — 조건 추가?`}
          onConfirm={() => { confirmStep(activePopup); advancePopup(); }}
          onClose={closePopup}
          progress={popupSeq ? popupProgress(activePopup, groups, templateKey) : null}
          {...backProps(activePopup)}
          hint="근무일수만 다른 조건이 더 있으면 여기서 복제하세요(엑셀 열 하나 늘리듯).">
          <div style={{ fontSize: 13, color: "#374151", marginBottom: 12 }}>
            이 분야에 조건을 더 추가할까요?
          </div>
          <button type="button"
            onClick={() => { confirmStep(activePopup); duplicateGroup(activeGroup.key); advancePopup(); }}
            style={{ width: "auto", padding: "0 16px", height: 34, fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
              border: "1px solid #93c5fd", borderRadius: 6, background: "#eff6ff", color: "#1d4ed8", cursor: "pointer" }}>
            + 조건 추가 (지금 조건을 복제)
          </button>
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 8 }}>
            추가하면 새 조건의 근무일수·시간을 이어서 묻습니다. 필요 없으면 아래 [확인]을 누르세요.
          </div>
        </StepPopup>
      )}
      {activeGrp && activeGroup && (activeGrp.kind === "welfare" || activeGrp.kind === "etc") && (() => {
        // 전체 배열의 인덱스를 살려서 거른다 — setGroupOtherField가 원본 idx를 받는다.
        const rows = activeGroup.other_items
          .map((o, idx) => ({ o, idx }))
          .filter(({ o }) => (activeGrp.kind === "welfare"
            ? (o.block || "etc") === "welfare"
            : (o.block || "etc") !== "welfare"));
        const fillable = templateKey && missingTemplateItems(activeGroup, templateKey, months).length > 0;
        const isWelfare = activeGrp.kind === "welfare";
        return (
        <StepPopup
          size="wide"
          title={`${activeGroup.name || activeGroup.fieldName} — ${isWelfare ? "복리후생" : "기타"}`}
          onConfirm={() => { confirmStep(activePopup); advancePopup(); }}
          onClose={closePopup}
          progress={popupSeq ? popupProgress(activePopup, groups, templateKey) : null}
          {...backProps(activePopup)}
          hint={isWelfare
            ? "피복비·명절 — 단가와 횟수만 확인하면 됩니다."
            : "재료비 월액을 넣으세요. 안전관리비는 비율이라 자동 계산됩니다."}>
          {/* ⚠️ 복리후생은 공단 기본값(50,000원×연2회)이 그대로 맞는 경우가 대부분이다.
              머뭇거리지 않게 "확인만 하면 된다"를 먼저 알린다. 기타는 반대로 재료비
              월액이 사업장마다 달라 매번 입력이 필요하다. */}
          <div style={{ fontSize: 12, color: "#1e40af", background: "#eff6ff",
            border: "1px solid #bfdbfe", borderRadius: 6, padding: "6px 10px", marginBottom: 10 }}>
            {isWelfare
              ? "기본값이 맞으면 그대로 [확인]을 누르세요."
              : "재료비 월액은 사업장마다 다릅니다 — 금액 칸을 채워주세요."}
          </div>
          {fillable && (
            <button type="button" onClick={() => fillGongdanTemplate(activeGroup.key)}
              style={{ width: "auto", padding: "0 14px", height: 30, fontSize: 12, whiteSpace: "nowrap",
                border: "1px solid #93c5fd", borderRadius: 6, background: "#eff6ff", color: "#1d4ed8",
                cursor: "pointer", marginBottom: 10 }}>
              공단 기본 4항목 채우기
            </button>
          )}
          {rows.length === 0 && (
            <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 8 }}>
              이 조건에 {isWelfare ? "복리후생" : "기타"} 항목이 없습니다 — 없으면 그대로 [확인].
            </div>
          )}
          {rows.map(({ o, idx }) => (
            <OtherItemRow key={o.id || idx} o={o} idx={idx} readOnly={readOnly}
              setOtherField={(i, f, v) => setGroupOtherField(activeGroup.key, i, f, v)}
              removeOtherItem={(i) => removeGroupOtherItem(activeGroup.key, i)} />
          ))}
          <button type="button" onClick={() => addGroupOtherItem(activeGroup.key)} disabled={readOnly}
            style={{ width: "auto", padding: "0 12px", height: 28, fontSize: 12, whiteSpace: "nowrap",
              border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", color: "#374151", cursor: "pointer" }}>
            + 항목 추가
          </button>
        </StepPopup>
        );
      })()}
      {safetyNoneConfirm && (
        <ConfirmModal
          title="안전관리비 미적용으로 변경"
          body={"안전관리비 항목이 삭제됩니다.\n되돌리려면 항목을 다시 추가해야 합니다."}
          confirmLabel="삭제하고 변경"
          onClose={() => setSafetyNoneConfirm(null)}
          onConfirm={confirmSafetyNone}
        />
      )}
      {dupModal && (
        <DuplicateCaseModal
          onClose={() => setDupModal(false)}
          onGoList={() => navigate("/quotation", { state: { caseId: dupCaseId } })}
        />
      )}
      {activePopup === "title" && (
        <TitlePopup
          title={title} setTitle={(v) => { setTitle(v); setTitleTouched(true); }}
          year={year} siteName={siteName}
          duplicate={dupCaseId != null}
          onConfirm={() => {
            // 중복이면 다음 단계로 넘기지 않고 안내 모달을 띄운다.
            if (dupCaseId != null) { setDupModal(true); return; }
            confirmStep("title"); advancePopup();
          }} onClose={closePopup}
          progress={popupSeq ? popupProgress("title", groups, templateKey) : null} {...backProps("title")}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ⑥~⑩ 공용 컨트롤 — 팝업과 Step1이 "같은 컴포넌트"를 렌더한다.
//
// ⚠️ 두 곳에 같은 UI를 따로 두지 않는다. 값(상태)은 원래 QuotationForm에 하나뿐이라
// 이중화가 아니었지만, 마크업을 복사하면 옵션·문구가 언젠가 갈라진다(이 저장소가
// constants.py·WELFARE_KEYWORDS에서 반복해 겪은 유형). 그래서 컨트롤만 뽑아 공유한다.
// ══════════════════════════════════════════════════════════════

const MONTH_PRESETS = [12, 11.5, 10, 7.5, 6, 3];

function MonthsField({ months, setMonths, readOnly }) {
  const on = (m) => String(months) === String(m);
  return (
    <div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
        {MONTH_PRESETS.map((m) => (
          <button key={m} type="button" disabled={readOnly} onClick={() => setMonths(String(m))}
            style={{ width: "auto", flex: "0 0 auto", padding: "0 12px", height: 30, fontSize: 12,
              whiteSpace: "nowrap", borderRadius: 6, cursor: readOnly ? "default" : "pointer",
              border: on(m) ? "1px solid #0b5fff" : "1px solid #d1d5db",
              background: on(m) ? "#eff6ff" : "#fff",
              color: on(m) ? "#0b5fff" : "#374151", fontWeight: on(m) ? 700 : 400 }}>
            {fmtMonths(m)}개월
          </button>
        ))}
      </div>
      <input type="number" step="0.5" value={months} onChange={(e) => setMonths(e.target.value)}
        style={INPUT} disabled={readOnly} placeholder="직접입력(예: 7.5)" />
    </div>
  );
}

function WageField({ wageRates, hourlyWage, setHourlyWage, readOnly }) {
  return (
    <div style={{ display: "flex", gap: 6 }}>
      <select
        value={wageRates.some((w) => String(w.hourly) === String(hourlyWage)) ? hourlyWage : ""}
        onChange={(e) => {
          const lw = wageRates.find((w) => String(w.hourly) === e.target.value);
          if (lw) setHourlyWage(String(lw.hourly));   // 지자체는 건드리지 않는다
        }}
        style={{ ...INPUT, flex: 1 }} disabled={readOnly}
      >
        <option value="">등록값 선택</option>
        {wageRates.map((w) => (
          <option key={w.id} value={w.hourly}>
            {String(w.grade || "").includes(String(w.year)) ? w.grade : `${w.year}년 ${w.grade}`}
            {" "}{Number(w.hourly).toLocaleString()}원
          </option>
        ))}
      </select>
      <input type="number" value={hourlyWage} onChange={(e) => setHourlyWage(e.target.value)}
        style={{ ...INPUT, width: 120, flexShrink: 0 }} disabled={readOnly} placeholder="직접입력" />
    </div>
  );
}

// ⑧ 생활임금 지자체 — 3택. 값이 그대로 출력 문구 조각이 된다
// ("2027년 예상 생활임금 (12,900원)" — xlsx_export 표지 B35 / calc.build_base_sheet).
//
// ⚠️ "예상"을 고를 때만 시급을 덮어쓴다. 다른 선택지는 시급과 무관하다 — 지자체는
// 문구용이고 시급은 ⑦에서 정하는 별개 값이라, 매번 덮어쓰면 ⑦의 선택이 조용히 사라진다.
// ⚠️ 3택에 없는 값(과거 견적서의 자유입력 지자체)은 직접입력 칸으로 그대로 보인다 —
// 선택지를 셋으로 좁히면서 기존 데이터를 잃지 않기 위함.
function RegionField({ wageRegion, setWageRegion, expectedHourly, hourlyWage, setHourlyWage, readOnly }) {
  const isExpected = wageRegion === "예상";
  const isPreset = REGION_CHOICES.includes(wageRegion);
  return (
    <div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
        {REGION_CHOICES.map((r) => (
          <button key={r} type="button" disabled={readOnly}
            onClick={() => {
              setWageRegion(r);
              // 예상으로 바꾸는 순간, 마스터에 등록된 예상 금액이 있으면 시급에 반영한다.
              if (r === "예상" && expectedHourly) setHourlyWage(expectedHourly);
            }}
            style={{ width: "auto", flex: "0 0 auto", padding: "0 12px", height: 30, fontSize: 12,
              whiteSpace: "nowrap", borderRadius: 6, cursor: readOnly ? "default" : "pointer",
              border: wageRegion === r ? "1px solid #0b5fff" : "1px solid #d1d5db",
              background: wageRegion === r ? "#eff6ff" : "#fff",
              color: wageRegion === r ? "#0b5fff" : "#374151",
              fontWeight: wageRegion === r ? 700 : 400 }}>
            {r} 생활임금
          </button>
        ))}
      </div>
      {isExpected && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap", flexShrink: 0 }}>예상 금액</span>
          <input type="number" value={hourlyWage} onChange={(e) => setHourlyWage(e.target.value)}
            style={{ ...INPUT, flex: 1 }} disabled={readOnly}
            placeholder={expectedHourly ? `등록값 ${Number(expectedHourly).toLocaleString()}원` : "시급 직접 입력"} />
          <span style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap", flexShrink: 0 }}>원</span>
        </div>
      )}
      {!isPreset && (
        <input value={wageRegion} onChange={(e) => setWageRegion(e.target.value)}
          style={INPUT} disabled={readOnly} placeholder="예: 금천구 (견적서 비고란 표기용)" />
      )}
    </div>
  );
}

// ⑨ 4대보험 요율 — 연도별 세트 선택.
//
// ⚠️ 연도를 하드코딩하지 않는다. 견적 연도가 마스터에 없으면 "가장 최근 등록 연도"로
// 대체되는데(백엔드 insurance for-year가 그렇게 동작하고 matched_year를 함께 준다),
// 그 사실을 선택지로 미리 보여준다: "{견적연도}년 요율 미정({최근등록연도}년 동일)".
// (N-1)년으로 잡지 않은 이유는 실제로 그 해가 없을 수 있기 때문이다 — 2026년만
// 등록된 상태에서 2029년 견적이면 2028년도 없다.
//
// 선택하면 applyRateSet이 값을 견적서 자체 컬럼에 복사한다(Quotation.pension_rate 등이
// 전부 "스냅샷" 컬럼이고 InsuranceRate로의 FK가 없다) — 원본 참조가 아니라서 나중에
// 마스터가 바뀌어도 이 견적서 값은 움직이지 않는다.
function RateSetField(props) {
  const {
    year, rateSets, rateApplied, rateModified, rateSnapshot, applyRateSet,
    pensionRate, setPensionRate, healthRate, setHealthRate, careRate, setCareRate,
    employRate, setEmployRate, injuryRate, setInjuryRate, readOnly,
    mode = "form", revealed = true,
  } = props;

  // ✅ mode="popup" 2단 공개 — 처음엔 드롭다운만 보이고, 세트를 고르면 그때 5칸과
  // 안내가 나타난다(사용자 확정 2026-08-20). Step 1 폼(mode="form")은 현행 유지:
  // 폼은 스캔하며 값을 확인하는 자리라 숨기면 오히려 불편하다.
  const showRates = mode !== "popup" || revealed;

  const latestYear = useMemo(
    () => rateSets.reduce((m, r) => Math.max(m, Number(r.year) || 0), 0), [rateSets]);
  const hasOwnYear = rateSets.some((r) => String(r.year) === String(year));
  const substituted = rateSnapshot && String(rateSnapshot.matchedYear) !== String(rateApplied);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        <select
          value={rateApplied || ""} disabled={readOnly}
          onChange={(e) => { if (e.target.value) applyRateSet(e.target.value); }}
          style={{ ...INPUT, width: 250, height: 30, fontSize: 12, flexShrink: 0 }}
        >
          <option value="">등록 요율 선택</option>
          {!hasOwnYear && latestYear > 0 && (
            <option value={String(year)}>{year}년 요율 미정({latestYear}년 동일)</option>
          )}
          {rateSets.map((r) => (
            <option key={r.id ?? r.year} value={r.year}>
              {r.year}년 요율
              {String(r.year) === String(rateApplied) && rateModified ? " (수정됨)" : ""}
            </option>
          ))}
        </select>
        {rateModified && !readOnly && (
          <button type="button" onClick={() => applyRateSet(rateApplied)}
            title="선택한 연도의 등록 요율로 되돌립니다"
            style={{ width: "auto", padding: "0 10px", height: 30, flexShrink: 0, fontSize: 11,
              whiteSpace: "nowrap", border: "1px solid #d1d5db", borderRadius: 6,
              background: "#fff", color: "#374151", cursor: "pointer" }}>
            다시 적용
          </button>
        )}
        {substituted && (
          <span style={{ fontSize: 11, color: "#b45309", whiteSpace: "nowrap" }}>
            {rateSnapshot.matchedYear}년 요율 준용
          </span>
        )}
      </div>
      {showRates && (<>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {[["국민연금", pensionRate, setPensionRate], ["건강보험", healthRate, setHealthRate],
          ["장기요양", careRate, setCareRate], ["고용보험", employRate, setEmployRate],
          ["산재보험", injuryRate, setInjuryRate]].map(([label, val, setter]) => (
          <div key={label} style={{ width: 76, flexShrink: 0 }}>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 2, whiteSpace: "nowrap" }}>{label}</div>
            <input type="number" step="0.001" value={val} onChange={(e) => setter(e.target.value)}
              style={{ ...INPUT, height: 30, padding: "0 6px", fontSize: 12 }} disabled={readOnly} />
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6, whiteSpace: "nowrap" }}>
        고용보험 = 근로자 부담분 + 고안직능 사업주율 · 산재는 사업장·업종마다 달라 수정 가능
      </div>
      </>)}
    </div>
  );
}

function FeeField({ generalAdminRate, setGeneralAdminRate, profitRate, setProfitRate, readOnly }) {
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {[["일반관리비", generalAdminRate, setGeneralAdminRate], ["이윤", profitRate, setProfitRate]].map(
        ([label, val, setter]) => (
          <div key={label} style={{ width: 110, flexShrink: 0 }}>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 2, whiteSpace: "nowrap" }}>{label}</div>
            <input type="number" step="0.1" value={val} onChange={(e) => setter(e.target.value)}
              style={{ ...INPUT, height: 30, padding: "0 8px", fontSize: 12 }} disabled={readOnly} />
          </div>
        ))}
    </div>
  );
}

// ⑥~⑩ 팝업 공통 껍데기 — 위 컨트롤을 그대로 담고 [확인]만 붙인다.
function StepPopup({ title, onConfirm, onClose, progress, onBack, backLabel, hint, size, children }) {
  return (
    <PopupShell title={title} onClose={onClose} progress={progress} onBack={onBack} backLabel={backLabel} size={size}>
      {children}
      {hint && (
        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 8, whiteSpace: "nowrap" }}>{hint}</div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
        <button type="button" style={SAVE_BTN} onClick={onConfirm}>확인</button>
      </div>
    </PopupShell>
  );
}

function StepIndicator({ step, gotoStep }) {
  const labels = ["기본 정보", "근무조건·수당", "복리후생·기타"];
  return (
    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
      {labels.map((lb, i) => {
        const n = i + 1;
        const active = step === n;
        return (
          <button
            key={n} type="button" onClick={() => gotoStep(n)}
            style={{
              height: 34, padding: "0 14px", fontSize: 12, whiteSpace: "nowrap", borderRadius: 16,
              border: active ? "1px solid #0b5fff" : "1px solid #d1d5db",
              background: active ? "#0b5fff" : "#fff", color: active ? "#fff" : "#6b7280",
              cursor: "pointer", fontWeight: active ? 700 : 400,
            }}
          >
            {n}. {lb}
          </button>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// 팝업 공용 셸 — 오버레이+박스+ESC/배경클릭 닫기(닫아도 이전 선택은 유지,
// 시퀀스만 중단 — onClose는 항상 closePopup을 받는다).
// ══════════════════════════════════════════════════════════════
// ✅ size="wide" — 복리후생·기타 항목 팝업 전용. 항목 행이 요소 7개+비고라
// 기본 480px에서는 "개월" 라벨이 잘리고 가로 스크롤바가 생겼다(실측).
// 정액형 행 최소 요구폭 ≈ 651px(요소 531 + gap 54 + 행 padding·border 18 +
// 팝업 padding 48)이라 720으로 잡아 flex 입력칸에 여유를 남긴다.
// ⚠️ 뷰포트보다 커지지 않게 max-width는 min(720px, 95vw). 다른 팝업은 현행 480.
const POPUP_WIDTH = { default: { width: 480, maxWidth: "90vw" },
                      wide: { width: 720, maxWidth: "min(720px, 95vw)" } };

function PopupShell({ title, children, onClose, progress, onBack, backLabel, size = "default" }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); onClose(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // 배경 닫기는 훅이 판정한다(드래그로 잘못 닫히는 문제 — useBackdropClose 주석 참고).
  // ESC와 X 버튼은 그대로다.
  const backdrop = useBackdropClose(onClose);

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
      {...backdrop}
    >
      <div style={{ background: "#fff", borderRadius: 10, padding: 24, ...(POPUP_WIDTH[size] || POPUP_WIDTH.default), maxHeight: "80vh", overflowY: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <strong style={{ fontSize: 15, whiteSpace: "nowrap" }}>{title}</strong>
          <button type="button" onClick={onClose} title="닫기(여기까지 고른 값은 유지됩니다)"
            style={{ width: 28, height: 28, flexShrink: 0, border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", cursor: "pointer" }}>
            ✕
          </button>
        </div>
        {/* 진행 표시 — 칸 하나 = 구간 하나(기본 정보 / 견적 조건 / 조건별).
            스텝을 그대로 칸으로 그리면 조건이 늘 때마다 칸이 늘어 실처럼 가늘어지고
            진행률이 역행한다. 구간 단위면 지난 구간은 채워진 채로 남는다.
            단독 [변경] 호출과 제수당 팝업(구간 밖)에는 안 나온다. */}
        {progress ? (
          <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 16 }}>
            {progress.segments.map((label, i) => (
              <span key={`${label}-${i}`} style={{
                flex: 1, height: 4, borderRadius: 2,
                background: i === progress.segIdx ? "#0b5fff" : i < progress.segIdx ? "#93c5fd" : "#e5e7eb",
              }} title={label} />
            ))}
            <span style={{ fontSize: 11, color: "#9ca3af", whiteSpace: "nowrap", marginLeft: 6 }}>
              {progress.label} {progress.inSeg}/{progress.inSegCount}
            </span>
          </div>
        ) : <div style={{ marginBottom: 12 }} />}
        {children}
        {onBack && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #f1f5f9" }}>
            <button type="button" onClick={onBack}
              style={{ width: "auto", padding: "0 12px", height: 28, fontSize: 12, whiteSpace: "nowrap",
                border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", color: "#6b7280", cursor: "pointer" }}>
              ← 이전 ({backLabel})
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ① 연도 팝업
function YearPopup({ year, setYear, onConfirm, onClose, progress, onBack, backLabel }) {
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); inputRef.current?.select(); }, []);
  return (
    <PopupShell title="① 연도 선택" onClose={onClose} progress={progress} onBack={onBack} backLabel={backLabel}>
      <input
        ref={inputRef} type="number" value={year}
        onChange={(e) => setYear(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onConfirm(); } }}
        style={{ ...INPUT, height: 48, fontSize: 20, textAlign: "center" }}
      />
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
        <button type="button" style={SAVE_BTN} onClick={onConfirm}>확인</button>
      </div>
    </PopupShell>
  );
}

// ② 발주처 팝업 — type 화면 → (금천구청)dept / (기타)other 화면으로 같은 팝업 안에서 전환
function InstitutionPopup(props) {
  const {
    instSub, setInstSub, institutionType, chooseInstitutionType,
    institutionDept, setInstitutionDept, deptOptions, deptDirectInput, setDeptDirectInput,
    institutionOther, setInstitutionOther, otherOptions, otherDirectInput, setOtherDirectInput,
    onConfirmDept, onConfirmOther, onClose, progress, onBack, backLabel,
  } = props;

  return (
    <PopupShell title="② 발주처 선택" onClose={onClose} progress={progress} onBack={onBack} backLabel={backLabel}>
      {instSub === "type" && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {INSTITUTION_TYPES.map((t) => (
            <button key={t} type="button" style={BIG_CHOICE_BTN(institutionType === t)} onClick={() => chooseInstitutionType(t)}>
              {t}
            </button>
          ))}
        </div>
      )}

      {instSub === "dept" && (
        <div>
          <button type="button" style={BACK_BTN} onClick={() => setInstSub("type")}>← 발주처 다시 선택</button>
          <div style={{ marginTop: 12 }}>
            {!deptDirectInput ? (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                {deptOptions.map((d) => (
                  <button key={d} type="button" style={CHOICE_BTN(institutionDept === d)} onClick={() => setInstitutionDept(d)}>{d}</button>
                ))}
                <button type="button" style={CHOICE_BTN(false)} onClick={() => setDeptDirectInput(true)}>직접입력</button>
              </div>
            ) : (
              <div style={{ marginBottom: 12 }}>
                <input
                  autoFocus value={institutionDept} onChange={(e) => setInstitutionDept(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && institutionDept.trim()) { e.preventDefault(); onConfirmDept(); } }}
                  style={{ ...INPUT, marginBottom: 6 }} placeholder="예: 청소행정과"
                />
                <button type="button" style={BACK_BTN} onClick={() => setDeptDirectInput(false)}>버튼 목록으로</button>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button type="button" style={SAVE_BTN} disabled={!institutionDept.trim()} onClick={onConfirmDept}>확인</button>
            </div>
          </div>
        </div>
      )}

      {instSub === "other" && (
        <div>
          <button type="button" style={BACK_BTN} onClick={() => setInstSub("type")}>← 발주처 다시 선택</button>
          <div style={{ marginTop: 12 }}>
            {!otherDirectInput ? (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                {otherOptions.map((o) => (
                  <button key={o} type="button" style={CHOICE_BTN(institutionOther === o)} onClick={() => setInstitutionOther(o)}>{o}</button>
                ))}
                <button type="button" style={CHOICE_BTN(false)} onClick={() => setOtherDirectInput(true)}>직접입력</button>
              </div>
            ) : (
              <div style={{ marginBottom: 12 }}>
                <input
                  autoFocus value={institutionOther} onChange={(e) => setInstitutionOther(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && institutionOther.trim()) { e.preventDefault(); onConfirmOther(); } }}
                  style={{ ...INPUT, marginBottom: 6 }} placeholder="발주 기관명 직접 입력"
                />
                <button type="button" style={BACK_BTN} onClick={() => setOtherDirectInput(false)}>버튼 목록으로</button>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button type="button" style={SAVE_BTN} disabled={!institutionOther.trim()} onClick={onConfirmOther}>확인</button>
            </div>
          </div>
        </div>
      )}
    </PopupShell>
  );
}

// ③ 근무지 팝업 — 선택한 발주처의 계약 기반 버튼 그리드(+검색) + 직접입력
function SitePopup(props) {
  const {
    contracts, institutionType, institutionDept, institutionOther, contractId, pickContractAndAdvance,
    siteName, setSiteName, siteDirectInput, setSiteDirectInput,
    siteSearch, setSiteSearch, onConfirmDirect, onClose, progress, onBack, backLabel,
  } = props;

  // ✅ classifyInstitution 완전일치 기준(2026-08-01 확정) — "금천구청 폴백
  // 금지": 0건이면 그대로 0건(직접입력 유도)이지, 무관한 계약을 대신 보여주지
  // 않는다. institutionType이 아직 없는 경우(편집 모드에서 팝업만 단독 재호출
  // — 발주처 선택 단계를 아예 거치지 않은 케이스)만 예외적으로 전체 목록을
  // 보여준다(이건 "폴백으로 무관한 데이터가 섞이는" 버그가 아니라,애초에
  // 분류 기준 자체가 없는 경우의 최소한의 대비).
  const filtered = useMemo(() => {
    let list = contracts;
    if (institutionType) {
      list = contracts.filter((c) => {
        const cls = classifyInstitution(c.institution);
        if (cls.type !== institutionType) return false;
        if (institutionType === "금천구청") return cls.dept === institutionDept;
        if (institutionType === "기타") return cls.dept === institutionOther;
        return true; // 시설관리공단/문화재단은 type만 일치하면 충분
      });
    }
    if (siteSearch.trim()) {
      const q = siteSearch.trim().toLowerCase();
      list = list.filter((c) => deriveSiteName(c).toLowerCase().includes(q) || (c.service_name || "").toLowerCase().includes(q));
    }
    return list;
  }, [contracts, institutionType, institutionDept, institutionOther, siteSearch]);

  return (
    <PopupShell title="③ 근무지 선택" onClose={onClose} progress={progress} onBack={onBack} backLabel={backLabel}>
      {!siteDirectInput ? (
        <>
          {contracts.length > 6 && (
            <input
              autoFocus value={siteSearch} onChange={(e) => setSiteSearch(e.target.value)}
              style={{ ...INPUT, marginBottom: 10 }} placeholder="근무지 검색"
            />
          )}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", maxHeight: 300, overflowY: "auto", marginBottom: 4 }}>
            {filtered.length === 0 && (
              <span style={{ fontSize: 12, color: "#9ca3af" }}>일치하는 계약이 없습니다 — 직접입력을 이용하세요.</span>
            )}
            {filtered.map((c) => (
              <button key={c.id} type="button" style={CHOICE_BTN(contractId === c.id)} title={c.service_name} onClick={() => pickContractAndAdvance(c)}>
                {deriveSiteName(c)}
              </button>
            ))}
            <button type="button" style={CHOICE_BTN(false)} onClick={() => setSiteDirectInput(true)}>직접입력</button>
          </div>
        </>
      ) : (
        <div>
          <input
            autoFocus value={siteName} onChange={(e) => setSiteName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && siteName.trim()) { e.preventDefault(); onConfirmDirect(); } }}
            style={{ ...INPUT, marginBottom: 8 }} placeholder="근무지명 직접 입력"
          />
          <button type="button" style={{ ...BACK_BTN, marginBottom: 12 }} onClick={() => setSiteDirectInput(false)}>버튼 목록으로</button>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button type="button" style={SAVE_BTN} disabled={!siteName.trim()} onClick={onConfirmDirect}>확인</button>
          </div>
        </div>
      )}
    </PopupShell>
  );
}

// ④ 분야 팝업 — 다중 선택 + 새 분야 직접입력 등록 + 완료(Step2 그룹 카드로 연결)
function FieldPopup(props) {
  const { fieldCategories, groups, toggleField, newFieldInput, setNewFieldInput, addNewFieldCategory, addingField, onDone, onClose, progress, onBack, backLabel } = props;
  return (
    <PopupShell title="④ 분야 선택(다중 선택 가능)" onClose={onClose} progress={progress} onBack={onBack} backLabel={backLabel}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
        {fieldCategories.map((cat) => (
          <button key={cat.id} type="button" style={CHOICE_BTN(groups.some((g) => g.fieldName === cat.name))} onClick={() => toggleField(cat)}>
            {cat.name}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        <input
          value={newFieldInput} onChange={(e) => setNewFieldInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNewFieldCategory(); } }}
          style={{ ...INPUT, flex: 1 }} placeholder="목록에 없는 분야명 직접 입력 후 등록"
        />
        <button type="button" style={HEADER_BTN} disabled={addingField || !newFieldInput.trim()} onClick={addNewFieldCategory}>
          + 분야 등록
        </button>
      </div>
      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 14, whiteSpace: "nowrap" }}>
        선택된 분야: {groups.map((g) => g.name).join(", ") || "없음"}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button type="button" style={SAVE_BTN} onClick={onDone}>완료</button>
      </div>
    </PopupShell>
  );
}

function PopupSummaryLine({ label, value, onChange, disabled }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, minHeight: 28 }}>
      <div style={{ width: 60, flexShrink: 0, fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>{label}</div>
      <div style={{ flex: 1, fontSize: 13, color: "#111", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value || "-"}</div>
      {!disabled && (
        <button type="button" onClick={onChange}
          style={{ width: 52, height: 26, flexShrink: 0, fontSize: 11, borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer", whiteSpace: "nowrap" }}>
          변경
        </button>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Step 1(작성) — 팝업 요약 + 품명 + 계약개월/시급/보험료율 + 근무조건 그룹 카드
// ══════════════════════════════════════════════════════════════
// ⑤ 품명 팝업 — 근무지에서 자동 조립된 값이 기본으로 들어와 있어 대개 엔터만 치면 된다.
// ✅ 되돌릴 수 없는 변경 확인용 공용 모달.
// ⚠️ 배경 닫기는 useBackdropClose만 쓴다(오버레이 onClick 직접 금지).
function ConfirmModal({ title, body, confirmLabel, onClose, onConfirm }) {
  const backdrop = useBackdropClose(onClose);
  return (
    <div {...backdrop}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex",
               alignItems: "center", justifyContent: "center", zIndex: 1100 }}>
      <div style={{ background: "#fff", borderRadius: 10, padding: 22, width: 420, maxWidth: "92vw",
                    boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 10, whiteSpace: "nowrap" }}>{title}</div>
        <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.6, whiteSpace: "pre-line" }}>{body}</div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
          <button type="button" onClick={onClose}
            style={{ width: "auto", padding: "0 14px", height: 32, fontSize: 13, whiteSpace: "nowrap",
                     border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", cursor: "pointer" }}>
            취소
          </button>
          <button type="button" onClick={onConfirm}
            style={{ width: "auto", padding: "0 16px", height: 32, fontSize: 13, fontWeight: 600,
                     whiteSpace: "nowrap", border: "none", borderRadius: 6, background: "#dc2626",
                     color: "#fff", cursor: "pointer" }}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}


// ✅ 중복 견적서 안내 — 케이스를 만들지 않고 기존 건으로 유도한다.
// ⚠️ 배경 닫기는 useBackdropClose만 쓴다(오버레이 onClick 직접 금지).
function DuplicateCaseModal({ onClose, onGoList }) {
  const backdrop = useBackdropClose(onClose);
  return (
    <div {...backdrop}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex",
               alignItems: "center", justifyContent: "center", zIndex: 1100 }}>
      <div style={{ background: "#fff", borderRadius: 10, padding: 22, width: 440, maxWidth: "92vw",
                    boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 10, whiteSpace: "nowrap" }}>
          이미 작성된 품명입니다
        </div>
        <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.6 }}>
          해당 품명은 이미 작성되어 있습니다.<br />
          기존 견적서에서 이어서 작성하거나 새 버전을 만들어 주세요.
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
          <button type="button" onClick={onClose}
            style={{ width: "auto", padding: "0 14px", height: 32, fontSize: 13, whiteSpace: "nowrap",
                     border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", cursor: "pointer" }}>
            닫기
          </button>
          <button type="button" onClick={onGoList}
            style={{ width: "auto", padding: "0 16px", height: 32, fontSize: 13, fontWeight: 600,
                     whiteSpace: "nowrap", border: "none", borderRadius: 6, background: "#0b5fff",
                     color: "#fff", cursor: "pointer" }}>
            견적서 목록으로 이동
          </button>
        </div>
      </div>
    </div>
  );
}


function TitlePopup({ title, setTitle, year, siteName, duplicate, onConfirm, onClose, progress, onBack, backLabel }) {
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); inputRef.current?.select(); }, []);
  const suggested = siteName ? `${year}년 ${siteName} 용역` : "";
  return (
    <PopupShell title="⑤ 품명 확인" onClose={onClose} progress={progress} onBack={onBack} backLabel={backLabel}>
      <input
        ref={inputRef} value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onConfirm(); } }}
        style={{ ...INPUT, height: 44, fontSize: 15 }}
        placeholder="예: 2027년 구립도서관 야간 경비 용역"
      />
      {duplicate ? (
        <div style={{ fontSize: 12, color: "#b91c1c", background: "#fef2f2",
          border: "1px solid #fecaca", borderRadius: 6, padding: "6px 10px", marginTop: 8 }}>
          같은 발주처·연도·품명의 견적서가 이미 있습니다 — 확인을 누르면 안내가 나옵니다.
        </div>
      ) : (
        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6, whiteSpace: "nowrap" }}>
          근무지에서 자동 조립된 값입니다 — 그대로 쓰려면 [확인](또는 엔터), 고치려면 위 칸을 수정하세요.
        </div>
      )}
      {suggested && title !== suggested && (
        <button type="button" onClick={() => setTitle(suggested)}
          style={{ width: "auto", padding: "0 10px", height: 26, marginTop: 8, fontSize: 11, whiteSpace: "nowrap",
            border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", color: "#374151", cursor: "pointer" }}>
          자동값으로 되돌리기 ({suggested})
        </button>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
        <button type="button" style={SAVE_BTN} onClick={onConfirm}>확인</button>
      </div>
    </PopupShell>
  );
}

function MainStep(props) {
  const {
    step, gongdanFill,
    safetyMethod, applySafetyMethod, safetyUnitPrice, setSafetyUnitPrice,
    safetyPeriod, setSafetyPeriod,
    isEdit, readOnly, institutionDisplay, institutionName,
    year, siteName, title, setTitle, months, setMonths,
    wageRates, hourlyWage, setHourlyWage, wageRegion, setWageRegion, expectedHourly,
    pensionRate, setPensionRate, healthRate, setHealthRate, careRate, setCareRate,
    employRate, setEmployRate, injuryRate, setInjuryRate,
    rateSets, applyRateSet, rateApplied, rateModified, rateSnapshot,
    generalAdminRate, setGeneralAdminRate, profitRate, setProfitRate,
    annualRounddown, setAnnualRounddown,
    missingPopupStep, missingPopupSteps, resumePopupSequence,
    groups, openPopup, duplicateGroup, restoreAutoHours,
    removeGroup, setGroupField, setGroupAllowanceField,
    addGroupOtherItem, removeGroupOtherItem, setGroupOtherField,
  } = props;

  return (
    <div>
      {/* ── 1단계: 기본 정보 ─────────────────────────────────── */}
      {step === 1 && (<>
      {/* ✅ 진입 플로우를 중간에 닫았으면 여기서 이어갈 수 있다.
          전부 채워졌으면 나오지 않는다(재개할 게 없으므로). */}
      {missingPopupStep && !readOnly && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
          border: "1px solid #bfdbfe", background: "#eff6ff", borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
          {/* 남은 단계를 개수와 이름으로 함께 보여준다 — 하나 채울 때마다 줄어들고
              전부 채우면 이 배너 자체가 사라진다(자동으로 팝업을 띄우지는 않는다). */}
          <span style={{ fontSize: 12, color: "#1e40af", whiteSpace: "nowrap" }}>
            남은 {missingPopupSteps.length}단계 — {missingPopupSteps.map((n) => popupLabel(n, groups)).join(" · ")}
          </span>
          <button type="button" onClick={resumePopupSequence}
            style={{ width: "auto", padding: "0 14px", height: 30, flexShrink: 0, fontSize: 12, fontWeight: 600,
              whiteSpace: "nowrap", border: "none", borderRadius: 6, background: "#0b5fff", color: "#fff", cursor: "pointer" }}>
            이어서 설정하기 ({popupLabel(missingPopupStep, groups)}부터)
          </button>
        </div>
      )}
      <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", marginBottom: 14, background: "#f9fafb" }}>
        <PopupSummaryLine label="연도" value={`${year}년`} onChange={() => openPopup("year")} disabled={readOnly} />
        <PopupSummaryLine label="발주처" value={isEdit ? institutionDisplay : institutionName} onChange={() => openPopup("institution")} disabled={isEdit || readOnly} />
        <PopupSummaryLine label="근무지" value={siteName} onChange={() => openPopup("site")} disabled={readOnly} />
        <PopupSummaryLine label="분야" value={groups.map((g) => g.name).join(", ")} onChange={() => openPopup("field")} disabled={readOnly} />
      </div>

      <div style={ROW_GRID}>
        <div style={LABEL}>품명</div>
        <input value={title} onChange={(e) => setTitle(e.target.value)} style={INPUT} disabled={readOnly} placeholder="근무지를 고르면 자동 조립(수정 가능)" />
      </div>

      <div style={ROW_GRID}>
        <div style={LABEL}>계약개월</div>
        <MonthsField months={months} setMonths={setMonths} readOnly={readOnly} />
      </div>

      <div style={ROW_GRID}>
        <div style={LABEL}>시급</div>
        <WageField wageRates={wageRates} hourlyWage={hourlyWage} setHourlyWage={setHourlyWage} readOnly={readOnly} />
      </div>

      {/* ✅ 생활임금 지자체 — 견적서 비고란("2027년 금천구 생활임금 (12,727원)")에 쓰인다.
          시급 마스터(payroll WageRate)에는 지자체 개념이 없어 등급명으로 채우면 문구가
          망가진다(…년 2026년 생활임금 생활임금). 그래서 여기서 직접 정한다. */}
      <div style={ROW_GRID}>
        <div style={LABEL}>생활임금 지자체</div>
        <RegionField wageRegion={wageRegion} setWageRegion={setWageRegion}
          expectedHourly={expectedHourly} hourlyWage={hourlyWage} setHourlyWage={setHourlyWage}
          readOnly={readOnly} />
      </div>

      <div style={{ fontWeight: 700, fontSize: 13, margin: "12px 0 6px", whiteSpace: "nowrap" }}>보험료율(%)</div>
      <div style={{ marginBottom: 14 }}>
        <RateSetField
          year={year} rateSets={rateSets} rateApplied={rateApplied}
          rateModified={rateModified} rateSnapshot={rateSnapshot} applyRateSet={applyRateSet}
          pensionRate={pensionRate} setPensionRate={setPensionRate}
          healthRate={healthRate} setHealthRate={setHealthRate}
          careRate={careRate} setCareRate={setCareRate}
          employRate={employRate} setEmployRate={setEmployRate}
          injuryRate={injuryRate} setInjuryRate={setInjuryRate}
          readOnly={readOnly}
        />
      </div>

      {/* ✅ 안전관리비 방식 — 발주처마다 다르다. 기본값은 발주처에서 오지만
          항상 보이고 건별로 고칠 수 있다(기본값이 틀려도 조용히 지나가지 않게). */}
      <div style={{ fontWeight: 700, fontSize: 13, margin: "12px 0 6px" }}>안전관리비</div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
        {SAFETY_METHODS.map((m) => {
          const on = safetyMethod === m.value;
          return (
            <button key={m.value} type="button" disabled={readOnly} title={m.hint}
              onClick={() => applySafetyMethod(m.value)}
              style={{ width: "auto", padding: "0 12px", height: 30, fontSize: 12, whiteSpace: "nowrap",
                borderRadius: 6, border: on ? "1px solid #0b5fff" : "1px solid #d1d5db",
                background: on ? "#0b5fff" : "#fff", color: on ? "#fff" : "#374151",
                cursor: readOnly ? "default" : "pointer", flexShrink: 0 }}>
              {m.label}
            </button>
          );
        })}
        {safetyMethod === "per_person" && (<>
          <span style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>1인당</span>
          <input type="number" value={safetyUnitPrice} disabled={readOnly}
            onChange={(e) => setSafetyUnitPrice(Number(e.target.value) || 0)}
            style={{ ...INPUT, height: 30, width: 110, fontSize: 12 }} />
          <span style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>원 /</span>
          {[["monthly", "월"], ["annual", "연"]].map(([v, lb]) => (
            <button key={v} type="button" disabled={readOnly}
              onClick={() => setSafetyPeriod(v)}
              style={{ width: 44, height: 30, fontSize: 12, whiteSpace: "nowrap", borderRadius: 6,
                border: safetyPeriod === v ? "1px solid #0b5fff" : "1px solid #d1d5db",
                background: safetyPeriod === v ? "#0b5fff" : "#fff",
                color: safetyPeriod === v ? "#fff" : "#374151",
                cursor: readOnly ? "default" : "pointer", flexShrink: 0 }}>
              {lb}
            </button>
          ))}
        </>)}
      </div>

      {/* ✅ [3단계] 일반관리비·이윤율 — 발주처·용역마다 다르다(구 하드코딩 5%/8%) */}
      <div style={{ fontWeight: 700, fontSize: 13, margin: "12px 0 6px" }}>일반관리비·이윤율(%)</div>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 14 }}>
        <FeeField generalAdminRate={generalAdminRate} setGeneralAdminRate={setGeneralAdminRate}
          profitRate={profitRate} setProfitRate={setProfitRate} readOnly={readOnly} />
        <label style={{ display: "flex", alignItems: "center", gap: 6, height: 32, fontSize: 12, color: "#374151", whiteSpace: "nowrap", cursor: readOnly ? "default" : "pointer" }}>
          <input type="checkbox" checked={annualRounddown} disabled={readOnly}
            onChange={(e) => setAnnualRounddown(e.target.checked)}
            style={{ width: 16, height: 16, flexShrink: 0, margin: 0 }} />
          연간 총원가 10원 단위 절사(양식별 상이)
        </label>
      </div>

      </>)}

      {/* ── 2단계: 근무조건·수당 / 3단계: 복리후생·기타 ─────────── */}
      {step >= 2 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, margin: "0 0 8px" }}>
          <div style={{ fontWeight: 700, fontSize: 13, whiteSpace: "nowrap" }}>
            {step === 2 ? "근무조건·수당 — 조건 1개 = 견적서 열 1개" : "복리후생·기타 — 조건별 항목"}
          </div>
          <button type="button" onClick={() => openPopup("field")} disabled={readOnly}
            style={{ width: 88, height: 28, fontSize: 12, borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer", whiteSpace: "nowrap" }}>
            분야 변경
          </button>
        </div>
      )}
      {step >= 2 && groups.length === 0 && (
        <div style={{ padding: 20, textAlign: "center", color: "#9ca3af", border: "1px dashed #d1d5db", borderRadius: 8 }}>
          [분야 변경] 버튼으로 분야를 고르면 조건 카드가 여기 생깁니다.
        </div>
      )}
      {step >= 2 && groups.map((g) => (
        <GroupCard
          key={g.key} group={g} readOnly={readOnly} months={months}
          section={step === 2 ? "conditions" : "others"}
          onRemove={() => removeGroup(g.key)}
          onDuplicate={() => duplicateGroup(g.key)}
          onRestoreAuto={() => restoreAutoHours(g.key)}
          setField={(field, value) => setGroupField(g.key, field, value)}
          setAllowanceField={(idx, field, value) => setGroupAllowanceField(g.key, idx, field, value)}
          addOtherItem={() => addGroupOtherItem(g.key)}
          removeOtherItem={(idx) => removeGroupOtherItem(g.key, idx)}
          setOtherField={(idx, field, value) => setGroupOtherField(g.key, idx, field, value)}
          onFillGongdan={gongdanFill ? gongdanFill(g) : null}
        />
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Step 2 공용 컨트롤 — 팝업과 폼 카드가 같은 컴포넌트를 렌더한다(Step 1과 같은 방식).
// ══════════════════════════════════════════════════════════════

/** 조건 기본 — 인원·주N일·1일N시간·자동 환산시간·4대보험 면제 */
function GroupBasicField({ g, readOnly, setField, onRestoreAuto }) {
  const weeklyExempt = Number(g.weekly_hours) > 0 && Number(g.weekly_hours) < 15;
  const monthlyExempt = g.pension_health_care_exempt_override !== ""
    ? g.pension_health_care_exempt_override === "true"
    : (Number(g.monthly_hours) > 0 && Number(g.monthly_hours) < 60);
  const derived = deriveHours(g.days_per_week, g.hours_per_day);
  const overridden = !!derived && (
    String(derived.weekly) !== String(Number(g.weekly_hours))
    || String(derived.monthly) !== String(Number(g.monthly_hours))
  );
  return (
    <>
      {/* ✅ [3단계] 이 세 칸이 입력의 전부 — 실양식에서 "열 하나 복사해 일수만 고치는" 그 동작 */}
      <div style={{ display: "flex", gap: 10, marginBottom: 6 }}>
        {[["인원수", "personnel_count", 1], ["주 N일", "days_per_week", 0.5], ["1일 N시간", "hours_per_day", 0.5]].map(([label, field, step]) => (
          <div key={field} style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>{label}</div>
            <input type="number" step={step} value={g[field]} onChange={(e) => setField(field, e.target.value)}
              style={{ ...INPUT, height: 32 }} disabled={readOnly} />
          </div>
        ))}
      </div>

      {/* 자동 계산 결과 — 회색 배경으로 자동생성임을 드러내되 직접 수정도 허용 */}
      <div style={{ display: "flex", gap: 10, alignItems: "flex-end", marginBottom: 6 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>주당 근로시간(자동)</div>
          <input type="number" value={g.weekly_hours} onChange={(e) => setField("weekly_hours", e.target.value)}
            style={{ ...INPUT, height: 32, background: g.hoursTouched ? "#fff" : "#f3f4f6" }} disabled={readOnly} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>월 환산시간(자동)</div>
          <input type="number" value={g.monthly_hours} onChange={(e) => setField("monthly_hours", e.target.value)}
            style={{ ...INPUT, height: 32, background: g.hoursTouched ? "#fff" : "#f3f4f6" }} disabled={readOnly} />
        </div>
        {!readOnly && g.hoursTouched && derived && (
          <button type="button" onClick={onRestoreAuto}
            title="주 N일 × 1일 N시간 × 4.345주(올림)로 되돌리기"
            style={{ width: 74, height: 32, flexShrink: 0, fontSize: 11, whiteSpace: "nowrap", border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", color: "#374151", cursor: "pointer" }}>
            자동값 복원
          </button>
        )}
      </div>
      <div style={{ fontSize: 11, color: overridden ? "#b45309" : "#6b7280", marginBottom: 6, whiteSpace: "nowrap" }}>
        {derived
          ? (overridden
              ? `⚠️ 수동 보정됨 — 자동값은 주 ${derived.weekly}시간 / 월 ${derived.monthly}시간`
              : `주 ${derived.weekly}시간 × 4.345주 → 올림 ${derived.monthly}시간`)
          : "주 N일 · 1일 N시간을 넣으면 주당/월 환산시간이 자동으로 채워집니다"}
      </div>
      <div style={{ fontSize: 11, color: weeklyExempt || monthlyExempt ? "#b45309" : "#6b7280", marginBottom: 8, whiteSpace: "nowrap" }}>
        {weeklyExempt && "⚠️ 주 15시간 미만 — 연차수당·퇴직급여충당금 자동 미적용  "}
        {monthlyExempt && "⚠️ 월 60시간 미만 — 국민연금·건강·장기요양 자동 미적용"}
        {!weeklyExempt && !monthlyExempt && "15h/60h 기준 이상 — 전 항목 정상 적용"}
      </div>
      <div style={{ marginBottom: 10 }}>
        <select
          value={g.pension_health_care_exempt_override} disabled={readOnly}
          onChange={(e) => setField("pension_health_care_exempt_override", e.target.value)}
          style={{ ...INPUT, height: 30, fontSize: 12 }}
        >
          <option value="">4대보험 면제 자동(월 60h 기준)</option>
          <option value="true">강제 면제(0원 처리)</option>
          <option value="false">강제 적용(요율 그대로)</option>
        </select>
      </div>
    </>
  );
}

/** 제수당 1항목 — 6항목이 같은 구조라 카테고리별 차이는 ALLOWANCE_RULES가 담는다.
 *  하드코딩 분기는 연차의 면제 오버라이드 하나뿐이다. */
function AllowanceItemField({ a, idx, readOnly, setAllowanceField, weeklyExempt, months }) {
  const catLabel = ALLOWANCE_CATEGORIES.find((c) => c.value === a.category)?.label || a.category;
  const itemExempt = a.category === "annual" && (a.exempt_override !== null ? a.exempt_override : weeklyExempt);
  const rule = ruleFor(a.category);
  const mRule = rule.multiplier;
  const offRule = violatesRule(a);   // 옛 데이터가 규칙과 다른 경우
  return (
          <div key={a.category} style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: 8, marginBottom: 6, background: itemExempt ? "#f3f4f6" : "#fff" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <strong style={{ fontSize: 12, color: itemExempt ? "#9ca3af" : "#111", whiteSpace: "nowrap" }}>{catLabel}</strong>
              {/* 입력 방식이 규칙으로 고정된 항목은 선택 버튼을 아예 없앤다 */}
              {rule.mode ? (
                <span style={{ fontSize: 11, color: "#9ca3af", whiteSpace: "nowrap" }}>
                  {rule.mode === "param" ? "파라미터형 고정" : "정액형 고정"}
                </span>
              ) : (
                <div style={{ display: "flex", gap: 4 }}>
                  <button type="button" disabled={readOnly} onClick={() => setAllowanceField(idx, "input_mode", "fixed")}
                    style={{ width: 56, height: 24, fontSize: 11, borderRadius: 4, border: "1px solid #d1d5db", background: a.input_mode === "fixed" ? "#0b5fff" : "#fff", color: a.input_mode === "fixed" ? "#fff" : "#374151", whiteSpace: "nowrap" }}>정액형</button>
                  <button type="button" disabled={readOnly} onClick={() => setAllowanceField(idx, "input_mode", "param")}
                    style={{ width: 64, height: 24, fontSize: 11, borderRadius: 4, border: "1px solid #d1d5db", background: a.input_mode === "param" ? "#0b5fff" : "#fff", color: a.input_mode === "param" ? "#fff" : "#374151", whiteSpace: "nowrap" }}>파라미터형</button>
                </div>
              )}
            </div>
            {/* ⚠️ 옛 견적서가 규칙과 다른 값이면 알려준다 — 저장값은 그대로 보여주되
                (임의로 덮어쓰지 않는다), 재저장하면 normalizeAllowance가 정정한다. */}
            {offRule && (
              <div style={{ fontSize: 11, color: "#b45309", marginBottom: 4, whiteSpace: "nowrap" }}>
                ⚠️ 규칙과 다름 — 저장하면 규칙값으로 정정됩니다
              </div>
            )}
            {itemExempt && <div style={{ fontSize: 11, color: "#b45309", marginBottom: 4, whiteSpace: "nowrap" }}>주 15시간 미만 근무자 미적용(0원)</div>}
            {!itemExempt && a.input_mode === "fixed" ? (
              <input type="number" value={a.fixed_amount} onChange={(e) => setAllowanceField(idx, "fixed_amount", Number(e.target.value) || 0)}
                style={{ ...INPUT, height: 28 }} disabled={readOnly} placeholder="정액(원)" />
            ) : !itemExempt && (
              /* ✅ [3단계] "×주" 추가 — 실양식 야간근로수당의 "…*5일*4.345주" 자리.
                 기본 1이라 다른 항목의 문구·금액은 그대로다(회귀 없음).
                 ⚠️ "÷개월" 칸은 일부러 뺐다 — 제수당은 매달 발생하므로 항상 ÷1이다.
                 계약개월수를 여기 넣으면 월액이 그만큼 축소된다(실측: 3개월 계약에서
                 야간수당 131,664 → 43,888). 연 1~2회성 지급의 월할 안분은 제수당이
                 아니라 아래 "기타 항목"의 ÷개월이 담당한다(실양식 v3도 같은 구조 —
                 야간수당에 나누기 없음, 복리후생만 ÷개월). */
              <>
                {/* ✅ [시간][일수][×주][배율]을 한 줄에. ×주가 버튼 2개+직접입력이라
                    시간·일수 칸을 좁혀 자리를 만든다. 넘치면 부기만 둘째 줄로 흐른다. */}
                <div style={{ display: "flex", gap: 4, alignItems: "flex-end", flexWrap: "wrap" }}>
                  {[["시간", "param_hours", 0.5], ["일수", "param_days", 0.5]].map(([lb, key, step]) => (
                    <div key={key} style={{ width: 62, flexShrink: 0 }}>
                      <div style={{ fontSize: 10, color: "#9ca3af", whiteSpace: "nowrap" }}>{lb}</div>
                      <input type="number" step={step} value={a[key] == null ? "" : a[key]}
                        onChange={(e) => setAllowanceField(idx, key, Number(e.target.value) || 0)}
                        style={{ ...INPUT, height: 26, fontSize: 12, padding: "0 5px" }} disabled={readOnly} />
                    </div>
                  ))}

                  {/* ⚠️ 연차수당은 ×주를 렌더하지 않는다 — "시간×일수÷계약개월"
                      구조라 주 단위 반복이 성립하지 않는다(ALLOWANCE_RULES.weeks).
                      카테고리 하드코딩 대신 규칙 테이블로 갈랐다. */}
                  {rule.weeks !== false && (<>
                  {/* ×주 — 프리셋 버튼 + 직접입력. 저장값이 프리셋 밖이면 직접입력으로 열린다. */}
                  <div style={{ flexShrink: 0 }}>
                    <div style={{ fontSize: 10, color: "#9ca3af", whiteSpace: "nowrap" }}>×주</div>
                    <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
                      {WEEKS_PRESETS.map((w) => {
                        const on = Number(a.param_weeks) === w.value;
                        return (
                          <button key={w.value} type="button" disabled={readOnly}
                            onClick={() => setAllowanceField(idx, "param_weeks", w.value)}
                            title={`${w.label}주 (${w.sub})`}
                            style={{ width: "auto", height: 26, padding: "0 7px", fontSize: 11, borderRadius: 4,
                              border: on ? "1px solid #0b5fff" : "1px solid #d1d5db",
                              background: on ? "#0b5fff" : "#fff", color: on ? "#fff" : "#374151",
                              whiteSpace: "nowrap", cursor: readOnly ? "default" : "pointer", flexShrink: 0 }}>
                            {w.sub}
                          </button>
                        );
                      })}
                      {isWeeksPreset(a.param_weeks) ? (
                        <button type="button" disabled={readOnly}
                          onClick={() => setAllowanceField(idx, "param_weeks", 1)}
                          title="월 1회 등 변칙 주기 — 숫자를 직접 입력합니다"
                          style={{ width: "auto", height: 26, padding: "0 7px", fontSize: 11, borderRadius: 4,
                            border: "1px solid #d1d5db", background: "#fff", color: "#6b7280",
                            whiteSpace: "nowrap", cursor: readOnly ? "default" : "pointer", flexShrink: 0 }}>
                          직접
                        </button>
                      ) : (
                        <input type="number" step={0.005} value={a.param_weeks == null ? "" : a.param_weeks}
                          onChange={(e) => setAllowanceField(idx, "param_weeks", Number(e.target.value) || 0)}
                          style={{ ...INPUT, height: 26, width: 62, fontSize: 12, padding: "0 5px", flexShrink: 0 }}
                          disabled={readOnly} title="직접입력(주)" />
                      )}
                    </div>
                  </div>
                  </>)}

                  {/* ✅ 연차 분모 — 계약개월수 고정(calc가 quotation.months를 쓴다).
                      입력칸이 아니라 표기다: annual_own_divisor_months는 연차에 한해
                      계산에서 무시되므로 고칠 수 있는 것처럼 보이면 안 된다. */}
                  {a.category === "annual" && (
                    <div style={{ flexShrink: 0 }}>
                      <div style={{ fontSize: 10, color: "#9ca3af", whiteSpace: "nowrap" }}>÷개월</div>
                      <div style={{ height: 26, fontSize: 12, padding: "0 8px", display: "flex", alignItems: "center",
                        border: "1px solid #d1d5db", borderRadius: 6, background: "#f3f4f6", color: "#6b7280", whiteSpace: "nowrap" }}
                        title="연차 15일은 1년치라 계약기간에 안분한다 — 계약개월수로 자동">
                        /{fmtMonths(months || 12)}개월
                      </div>
                    </div>
                  )}

                  {/* 배율 — 규칙에 따라 고정 표시 / 선택지 / 자유 입력 */}
                  <div style={{ flexShrink: 0 }}>
                    <div style={{ fontSize: 10, color: "#9ca3af", whiteSpace: "nowrap" }}>배율</div>
                    {mRule && mRule.fixed !== undefined ? (
                      <div style={{ height: 26, fontSize: 12, padding: "0 8px", display: "flex", alignItems: "center",
                        border: "1px solid #d1d5db", borderRadius: 6, background: "#f3f4f6", color: "#6b7280", whiteSpace: "nowrap" }}>
                        {mRule.fixed}배 (고정)
                      </div>
                    ) : mRule && mRule.options ? (
                      <div style={{ display: "flex", gap: 3 }}>
                        {mRule.options.map((opt) => {
                          const on = Number(a.param_multiplier) === opt;
                          const lb = MULTIPLIER_LABEL[opt] || { main: opt + "배" };
                          return (
                            <button key={opt} type="button" disabled={readOnly}
                              onClick={() => setAllowanceField(idx, "param_multiplier", opt)}
                              title={lb.sub || ""}
                              style={{ width: "auto", height: 26, padding: "0 8px", fontSize: 11, borderRadius: 4,
                                border: on ? "1px solid #0b5fff" : "1px solid #d1d5db",
                                background: on ? "#0b5fff" : "#fff", color: on ? "#fff" : "#374151",
                                whiteSpace: "nowrap", cursor: readOnly ? "default" : "pointer", flexShrink: 0 }}>
                              {lb.main}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <input type="number" step={0.1} value={a.param_multiplier == null ? "" : a.param_multiplier}
                        onChange={(e) => setAllowanceField(idx, "param_multiplier", Number(e.target.value) || 0)}
                        style={{ ...INPUT, height: 26, width: 62, fontSize: 12, padding: "0 5px" }} disabled={readOnly} />
                    )}
                  </div>

                  {/* 부기 — 줄이 넘치면 여기만 둘째 줄로 떨어진다 */}
                  {MULTIPLIER_LABEL[Number(a.param_multiplier)] && MULTIPLIER_LABEL[Number(a.param_multiplier)].sub && (
                    <span style={{ fontSize: 10, color: "#9ca3af", whiteSpace: "nowrap", alignSelf: "center", paddingBottom: 5 }}>
                      {MULTIPLIER_LABEL[Number(a.param_multiplier)].sub}
                    </span>
                  )}
                </div>
              </>
            )}
            {a.category === "annual" && (
              <div style={{ marginTop: 4 }}>
                <select value={a.exempt_override === null ? "" : String(a.exempt_override)} disabled={readOnly}
                  onChange={(e) => setAllowanceField(idx, "exempt_override", e.target.value === "" ? null : e.target.value === "true")}
                  style={{ ...INPUT, height: 24, fontSize: 11 }}>
                  <option value="">면제 자동판정(주 15h 기준)</option>
                  <option value="true">강제 면제</option>
                  <option value="false">강제 적용</option>
                </select>
              </div>
            )}
          </div>
  );
}

function GroupCard({ group: g, readOnly, section = "conditions", months, onRemove, onDuplicate, onRestoreAuto, setField, setAllowanceField, addOtherItem, removeOtherItem, setOtherField, onFillGongdan }) {
  // ✅ 같은 카드를 단계별로 나눠 보여준다 — 상태는 부모(QuotationForm)에 있어
  // 어느 쪽을 감춰도 입력값이 날아가지 않는다.
  const showConditions = section === "conditions";
  const showOthers = section === "others";
  // 연차 면제 표시는 제수당 항목에 넘겨야 해서 카드에도 남긴다(나머지 파생값은
  // GroupBasicField 안으로 옮겼다 — 그 컴포넌트만 쓰는 값이다).
  const weeklyExempt = Number(g.weekly_hours) > 0 && Number(g.weekly_hours) < 15;

  return (
    <div style={{ border: "1px solid #d1d5db", borderRadius: 8, padding: 12, marginBottom: 14, background: "#fafbfc" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <input
          value={g.name} onChange={(e) => setField("name", e.target.value)}
          style={{ ...INPUT, flex: 1, fontWeight: 700, background: showOthers ? "#f9fafb" : undefined }}
          disabled={readOnly || showOthers} placeholder="조건명(주 N일을 넣으면 자동 조립)"
        />
        {!readOnly && showConditions && (
          <button type="button" onClick={onDuplicate} title="같은 분야에 조건 추가(엑셀 열 하나 늘리듯)"
            style={{ width: 92, height: 30, flexShrink: 0, fontSize: 12, whiteSpace: "nowrap", border: "1px solid #93c5fd", borderRadius: 6, background: "#fff", color: "#1d4ed8", cursor: "pointer" }}>
            + 조건 추가
          </button>
        )}
        {/* ⚠️ 삭제는 근무조건·복리후생 어느 단계에서도 보인다 — 3단계에서 조건을
            지우려면 2단계로 돌아가야 했다. 팝업 안에는 두지 않는다(연쇄 도중
            오삭제 방지, 사용자 확정 2026-08-19). */}
        {!readOnly && (
          <button type="button" title="조건 삭제"
            onClick={() => {
              if (window.confirm(`"${g.name || g.fieldName}" 조건을 삭제할까요?`)) onRemove();
            }}
            style={{ width: 30, height: 30, flexShrink: 0, border: "1px solid #fca5a5", borderRadius: 6, background: "#fff", color: "#dc2626", cursor: "pointer" }}>
            ✕
          </button>
        )}
      </div>

      {showConditions && (<>
      <GroupBasicField g={g} readOnly={readOnly} setField={setField} onRestoreAuto={onRestoreAuto} />

      <div style={{ fontWeight: 700, fontSize: 12, margin: "8px 0 6px" }}>제수당(6항목)</div>
      {g.allowance_items.map((a, idx) => (
        <AllowanceItemField key={a.category} a={a} idx={idx} readOnly={readOnly} months={months}
          setAllowanceField={setAllowanceField} weeklyExempt={weeklyExempt} />
      ))}
      </>)}

      {showOthers && (<>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "10px 0 6px" }}>
        <strong style={{ fontSize: 12, whiteSpace: "nowrap" }}>복리후생비·기타</strong>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {/* 발주처가 공단이고 이 조건에 4항목이 다 차 있지 않을 때만 보인다.
              같은 label은 건너뛰므로 눌러도 중복되지 않는다. 다 차면 버튼이 사라진다. */}
          {onFillGongdan && (
            <button type="button" onClick={onFillGongdan} disabled={readOnly}
              style={{ width: 132, height: 26, fontSize: 11, borderRadius: 6, border: "1px solid #93c5fd", background: "#eff6ff", color: "#1d4ed8", cursor: "pointer", whiteSpace: "nowrap" }}>
              공단 기본 4항목 채우기
            </button>
          )}
          <button type="button" onClick={addOtherItem} disabled={readOnly} style={{ width: 90, height: 26, fontSize: 11, borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer", whiteSpace: "nowrap" }}>+ 항목 추가</button>
        </div>
      </div>
      {g.other_items.length === 0 && (
        <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 6, whiteSpace: "nowrap" }}>
          등록된 항목이 없습니다 — [+ 항목 추가]로 피복비·작업화 등을 넣으세요.
        </div>
      )}
      {g.other_items.map((o, idx) => (
        <OtherItemRow key={o.id || idx} o={o} idx={idx} readOnly={readOnly}
          setOtherField={setOtherField} removeOtherItem={removeOtherItem} />
      ))}
      </>)}
    </div>
  );
}

// ✅ 복리후생·기타 항목 한 줄 — 조건 카드(Step 3 폼)와 항목 팝업이 같은 입력을
// 쓰도록 분리했다. 한쪽만 고치면 폼과 팝업이 서로 다른 항목을 편집하게 된다.
function OtherItemRow({ o, idx, readOnly, setOtherField, removeOtherItem }) {
  const isRate = o.amount_mode === "rate";
  // ✅ 발주처 제공 — 금액이 항상 0이라 단가·횟수·×인원 입력이 의미가 없다.
  // 칸을 없애지 않고 비활성(회색)으로 남긴다: 방식을 되돌렸을 때 원래 값이
  // 그대로 보여야 하고, 행이 갑자기 짧아졌다 길어졌다 하지 않는다.
  const isProvided = o.amount_mode === "provided";
  // ✅ 안전관리비는 상단 방식 토글(비율식/1인당 정액/미적용)이 금액 산출을 결정한다.
  // 항목 단에서 정액형/수식형을 또 고를 수 있으면 두 설정이 모순되므로 잠근다.
  const isSafety = isSafetyItem(o);
  return (
      <div key={o.id || idx} style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "6px 8px", marginBottom: 6 }}>
        {/* ⚠️ flexWrap — 더 좁은 환경(조건 카드 520px 컬럼, 좁은 노트북)에서 행이
            넘치면 가로 스크롤 대신 다음 줄로 접힌다. 라벨이 잘리지 않는 게 우선. */}
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <input value={o.label} onChange={(e) => setOtherField(idx, "label", e.target.value)} style={{ ...INPUT, height: 28, flex: 2, minWidth: 90, fontSize: 12 }} disabled={readOnly} placeholder="항목명(예: 피복비)" />
          {/* 소속 블록 — 엑셀 양식의 "복리후생비 / 기타" 두 블록 자리를 결정한다.
              수식형(안전관리비) 밑수가 "기타 블록의 정액 합"이라 금액에도 영향이 있다. */}
          <select value={o.block || "etc"} onChange={(e) => setOtherField(idx, "block", e.target.value)}
            style={{ ...INPUT, height: 28, width: 84, flexShrink: 0, fontSize: 12 }} disabled={readOnly} title="엑셀에서 들어갈 블록">
            <option value="welfare">복리후생</option>
            <option value="etc">기타</option>
          </select>
          {/* 안전관리비 배지 — 이 행이 왜 다르게 동작하는지(상단 토글을 따른다) 알린다. */}
          {isSafety && (
            <span title="상단 안전관리비 방식 설정을 따릅니다"
              style={{ fontSize: 11, fontWeight: 600, color: "#0b5fff", background: "#eff6ff",
                       border: "1px solid #bfdbfe", borderRadius: 4, padding: "2px 6px",
                       whiteSpace: "nowrap", flexShrink: 0, cursor: "help" }}>
              안전관리비
            </span>
          )}
          <select value={o.amount_mode || "fixed"} onChange={(e) => {
              const next = e.target.value;
              setOtherField(idx, "amount_mode", next);
              // ✅ 발주처 제공으로 바꾸면 비고를 채워 준다 — 비어 있을 때만.
              // 사용자가 적어 둔 비고를 덮지 않는다(수정 가능한 기본값이다).
              if (next === "provided" && !(o.remark || "").trim()) {
                setOtherField(idx, "remark", PROVIDED_REMARK);
              }
            }}
            style={{ ...INPUT, height: 28, width: 96, flexShrink: 0, fontSize: 12,
                     background: isSafety ? "#f3f4f6" : undefined, color: isSafety ? "#6b7280" : undefined }}
            disabled={readOnly || isSafety}
            title={isSafety ? "상단 안전관리비 방식 설정을 따릅니다" : "금액 산출 방식"}>
            <option value="fixed">정액형</option>
            <option value="rate">수식형</option>
            <option value="provided">발주처 제공</option>
          </select>
          {isRate ? (<>
            {/* 수식형 — 금액은 "(인건비 + 기타 블록 정액 합) × 비율%"로 정해진다.
                정액/횟수/나누는 개월수는 쓰이지 않으므로 칸 자체를 보여주지 않는다. */}
            <span style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>(인건비+기타)×</span>
            <input type="number" step="0.001" value={o.rate} onChange={(e) => setOtherField(idx, "rate", e.target.value)}
              style={{ ...INPUT, height: 28, width: 70, fontSize: 12 }} disabled={readOnly} title="비율(%)" />
            <span style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>%</span>
          </>) : (<>
            <input type="number" value={o.amount} onChange={(e) => setOtherField(idx, "amount", e.target.value)} style={{ ...INPUT, height: 28, flex: 1, minWidth: 70, fontSize: 12, background: isProvided ? "#f3f4f6" : undefined, color: isProvided ? "#9ca3af" : undefined }} disabled={readOnly || isProvided} placeholder="금액" title={isProvided ? "발주처 제공 — 금액은 0원입니다" : undefined} />
            <input type="number" value={o.count} onChange={(e) => setOtherField(idx, "count", e.target.value)} style={{ ...INPUT, height: 28, width: 50, flexShrink: 0, fontSize: 12, background: isProvided ? "#f3f4f6" : undefined, color: isProvided ? "#9ca3af" : undefined }} disabled={readOnly || isProvided} title={isProvided ? "발주처 제공 — 쓰이지 않습니다" : "지급 횟수"} />
            <span style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>회÷</span>
            <input type="number" value={o.divisor_months} onChange={(e) => setOtherField(idx, "divisor_months", e.target.value)} style={{ ...INPUT, height: 28, width: 50, flexShrink: 0, fontSize: 12, background: isProvided ? "#f3f4f6" : undefined, color: isProvided ? "#9ca3af" : undefined }} disabled={readOnly || isProvided} title={isProvided ? "발주처 제공 — 쓰이지 않습니다" : "나누는 개월수"} />
            <span style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>개월</span>
            {/* 재료비처럼 "그룹 전체 1건"인 항목은 월간 환산에서 인원을 곱하지 않는다.
                체크를 풀면 구성 문구에서도 "*N명"이 사라진다(xlsx_export가 같은 필드를 본다). */}
            <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#374151", whiteSpace: "nowrap", flexShrink: 0, cursor: readOnly ? "default" : "pointer" }}
              title="끄면 월간 환산에서 인원수를 곱하지 않는다(재료비 등 그룹 전체 1건)">
              <input type="checkbox" checked={!isProvided && o.scales_with_personnel !== false} disabled={readOnly || isProvided}
                onChange={(e) => setOtherField(idx, "scales_with_personnel", e.target.checked)}
                style={{ width: 14, height: 14, margin: 0 }} />
              ×인원
            </label>
          </>)}
          {!readOnly && <button type="button" onClick={() => removeOtherItem(idx)} style={{ width: 26, height: 26, flexShrink: 0, border: "1px solid #fca5a5", borderRadius: 6, background: "#fff", color: "#dc2626", cursor: "pointer" }}>✕</button>}
        </div>
        {/* 비고 — 엑셀 "비고" 열로 그대로 나간다(줄바꿈 유지). 구성 열 문구(note)와
            자리가 다르다: note에 비고를 넣으면 산식이 사라지고 월간 시트에서
            "반기별 작업복 지급*2명" 같은 문자열이 만들어진다. */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginTop: 4 }}>
          <span style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap", width: 30, flexShrink: 0, lineHeight: "22px" }}>비고</span>
          <textarea value={o.remark || ""} onChange={(e) => setOtherField(idx, "remark", e.target.value)} disabled={readOnly} rows={isRate ? 2 : 1}
            placeholder="엑셀 비고 열에 그대로 (줄바꿈 가능)"
            style={{ ...INPUT, flex: 1, minWidth: 0, height: "auto", fontSize: 11, padding: "3px 6px", resize: "vertical", fontFamily: "inherit" }} />
        </div>
      </div>
  );
}


// ══════════════════════════════════════════════════════════════
// Step 2 — 확인·저장
// ══════════════════════════════════════════════════════════════
function Step2({ isEdit, readOnly, saving, institutionDisplay, year, title, months, siteName, groups, recipient, setRecipient, onSave, onFinalize, onNewVersion, onExport }) {
  return (
    <div>
      <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 14, marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>입력 요약</div>
        <SummaryRow label="발주처" value={institutionDisplay || "-"} />
        {/* ✅ 표지에 "…귀중"으로 나가는 자리 — 규칙으로 채워 두고 여기서 고칠 수 있다.
            예외 케이스(본부 직접 계약 표기 등)를 손댈 유일한 지점이다. */}
        <div style={{ display: "flex", gap: 8, marginBottom: 4, fontSize: 13, alignItems: "center" }}>
          <div style={{ width: 70, color: "#6b7280", flexShrink: 0, whiteSpace: "nowrap" }}>수신처</div>
          <input value={recipient} onChange={(e) => setRecipient(e.target.value)} disabled={readOnly}
            placeholder="예: 금빛휘트니스센터 귀중"
            style={{ ...INPUT, height: 28, flex: 1, minWidth: 0, fontSize: 13 }} />
        </div>
        <SummaryRow label="연도" value={`${year}년`} />
        <SummaryRow label="근무지" value={siteName || "-"} />
        <SummaryRow label="품명" value={title || "-"} />
        <SummaryRow label="계약개월" value={`${fmtMonths(months)}개월`} />
        <SummaryRow label="분야" value={groups.map((g) => `${g.name}(${g.personnel_count}명)`).join(", ") || "-"} />
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {isEdit && <button type="button" style={HEADER_BTN} onClick={onExport}>📥 엑셀 다운로드</button>}
        {isEdit && !readOnly && <button type="button" style={HEADER_BTN} disabled={saving} onClick={onFinalize}>✅ 확정</button>}
        {isEdit && <button type="button" style={HEADER_BTN} disabled={saving} onClick={onNewVersion}>🔀 새 버전 만들기</button>}
        <button type="button" style={SAVE_BTN} disabled={saving} onClick={onSave}>{saving ? "저장 중…" : "저장"}</button>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 4, fontSize: 13 }}>
      <div style={{ width: 70, color: "#6b7280", flexShrink: 0, whiteSpace: "nowrap" }}>{label}</div>
      <div style={{ color: "#111" }}>{value}</div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   실시간 계산 미리보기 — 엑셀 파일 구조 그대로 시트 탭 방식
   (다운로드되는 워크북의 5시트와 1:1 대응: 견적서/기준단가/일위대가/월간/연간)

   ⚠️ 표시 레이어만 담당한다 — preview-unsaved가 돌려주는 계산 결과 구조는
   그대로 쓰고 아무것도 가공하지 않는다(계산 엔진은 calc.py 하나뿐이라는 원칙).
   ⚠️ 스타일은 전부 인라인 객체다. 이 프로젝트의 .module.css 요소 선택자가
   앱 전역으로 새는 반복 이슈(CLAUDE.md)가 있어, 새 클래스를 만들지 않고
   컴포넌트 안에 가둔다 — 다른 페이지 CSS에 영향이 없다.
   ══════════════════════════════════════════════════════════════════════════ */

const PREVIEW_SHEETS = ["견적서", "기준단가", "일위대가", "월간", "연간"];

// 엑셀 원가계산서 느낌 — 행 높이 22px, 셀 padding 2~4px, 12px, 행간 gap 없음
const PV = {
  panel: { border: "1px solid #e5e7eb", borderRadius: 8, position: "sticky", top: 12, overflow: "hidden" },
  head: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "10px 12px 8px" },
  title: { fontSize: 14, fontWeight: 700, whiteSpace: "nowrap" },
  tabBar: { display: "flex", gap: 2, padding: "0 8px", borderBottom: "1px solid #ddd", background: "#fafafa" },
  tab: (on) => ({
    height: 28, padding: "0 12px", fontSize: 12, whiteSpace: "nowrap", cursor: "pointer",
    border: "1px solid #ddd", borderBottom: on ? "1px solid #fff" : "1px solid #ddd",
    borderRadius: "5px 5px 0 0", marginBottom: -1,
    background: on ? "#fff" : "#f0f0f0", color: on ? "#111" : "#6b7280", fontWeight: on ? 700 : 400,
  }),
  body: { padding: 10, maxHeight: "calc(100vh - 160px)", overflowY: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12, lineHeight: 1.3, tableLayout: "fixed" },
  caption: { fontSize: 13, fontWeight: 700, color: "#111", margin: "8px 0 2px", whiteSpace: "nowrap" },
  th: { padding: "3px 4px", border: "1px solid #ddd", background: "#f5f5f5", fontWeight: 700, textAlign: "left", whiteSpace: "nowrap" },
  td: { padding: "2px 4px", border: "1px solid #ddd", height: 22, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  empty: { padding: 24, textAlign: "center", color: "#9ca3af", fontSize: 12 },
};

const INSURANCE_LABELS = [
  ["injury", "가. 산재보험료"], ["pension", "나. 국민연금"], ["health", "다. 건강보험"],
  ["employ", "라. 고용보험"], ["care", "마. 노인장기요양보험"],
];
// ⚠️ 이 키워드는 "분류 기준"이 아니라 "새 항목의 블록 기본값 추천"이다 —
// 백엔드 calc.suggest_block과 같은 규칙. 분류는 항목의 block 필드가 한다(blockOf).
// 라벨 매칭으로 분류하면 항목명을 "피복 재료비"로 적는 것만으로 사용자가 고른 블록이
// 뒤집히고, 수식형(안전관리비) 밑수가 "기타 블록 정액 합"이라 금액까지 조용히 달라진다.
const WELFARE_KEYWORDS = ["피복", "작업화", "명절", "복리"];
const KOREAN_ORDINALS = ["가", "나", "다", "라", "마", "바", "사", "아", "자", "차"];
const suggestBlock = (label) => (WELFARE_KEYWORDS.some((k) => (label || "").includes(k)) ? "welfare" : "etc");
// 미리보기 분류 = 백엔드 calc.is_welfare_item과 같은 기준(block 필드만 본다).
// block이 없는 구 데이터는 백엔드가 응답에 suggest_block 결과를 실어 보내므로 같은 추천으로 맞춘다.
const blockOf = (o) => o.block || suggestBlock(o.label);

// ⚠️ 미리보기 응답의 숫자는 전부 "문자열"이다(views._decimalize가 Decimal을 str로
// 직렬화 — 부동소수 오차 없이 그대로 표시하려고 일부러 그렇게 한다). 그래서 값끼리
// 더하면 문자열 연결이 돼 NaN이 된다("합계"가 "-"로 비던 원인). 표시 레이어에서
// 자체 계산하지 않는 게 원칙이고, 불가피한 소계만 이 함수로 숫자화해서 더한다.
const pvNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** 값 셀 하나 — 0원은 엑셀 양식과 행 수를 맞추려 행은 남기고 "-"로 표기. */
function PvCell({ value, text, strong, bg }) {
  const zero = text === undefined && pvNum(value) === 0;
  return (
    <td style={{
      ...PV.td, background: bg, fontWeight: strong ? 700 : 400,
      textAlign: "right", color: zero ? "#b0b0b0" : undefined,
    }}>
      {text !== undefined ? text : (zero ? "-" : won(value))}
    </td>
  );
}

/** 한 행 = 비목 1칸 + 조건 수만큼의 금액 칸(엑셀 E열/F열… 대응). */
function PvRow({ label, values, strong, indent = 0, texts }) {
  const bg = strong ? "#f5f5f5" : undefined;
  const cols = texts || values || [];
  return (
    <tr>
      <td style={{ ...PV.td, background: bg, fontWeight: strong ? 700 : 400, paddingLeft: 4 + indent * 11 }}>
        {label}
      </td>
      {cols.map((v, i) => (
        <PvCell key={i} strong={strong} bg={bg}
          {...(texts ? { text: v } : { value: v })} />
      ))}
    </tr>
  );
}

/** 일위대가/월간/연간 — 엑셀과 같은 열 배치: 비목 1열 + 조건마다 금액 열 1개.
 *  행 구조(가나다 순번, 0원 "-", 소계·계 강조)는 엑셀 양식의 행 순서 그대로. */
function CostSheet({ groups, level, months }) {
  if (!groups.length) return null;
  const key = level === "unit" ? "unit_cost" : level;
  const cols = groups.map((g) => g[key]);
  const T = cols.map((c) => c.totals);

  // ⚠️ 항목 라벨은 "전 조건 합집합"으로 만든다 — 조건마다 복리후생/기타 구성이
  // 다르면 순번으로 채울 때 다른 항목 행에 금액이 들어간다. 백엔드 엑셀 생성부
  // (_union_other_labels)와 같은 방식이라 미리보기와 엑셀이 어긋나지 않는다.
  // 그 조건에 없는 항목은 값이 없으므로 "-"로 표기된다.
  const splitOthers = (t) => {
    const others = t.other_lines || [];
    return [others.filter((o) => blockOf(o) === "welfare"), others.filter((o) => blockOf(o) !== "welfare")];
  };
  const unionLabels = (blockIdx) => {
    const seen = [];
    T.forEach((t) => splitOthers(t)[blockIdx].forEach((o) => {
      if (!seen.includes(o.label)) seen.push(o.label);
    }));
    return seen;
  };
  const welfareLabels = unionLabels(0);
  const etcLabels = unionLabels(1);
  const amountOf = (t, blockIdx, label) =>
    splitOthers(t)[blockIdx].find((o) => o.label === label)?.amount ?? 0;
  const sum = (rows) => rows.reduce((acc, o) => acc + pvNum(o.amount), 0);
  // 조건별로 같은 자리의 값을 뽑아 배열로 — 그대로 열이 된다.
  const pick = (fn) => T.map((t, i) => fn(t, cols[i]));

  const levelNote = level === "unit" ? "1인 1개월"
    : level === "monthly" ? "×인원" : `×인원 ×${fmtMonths(months)}개월`;

  return (
    <table style={PV.table}>
      <colgroup>
        <col style={{ width: `${Math.max(30, 56 - (groups.length - 1) * 12)}%` }} />
        {groups.map((g, i) => <col key={i} />)}
      </colgroup>
      <thead>
        <tr>
          <th style={PV.th} rowSpan={2}>비 목</th>
          {groups.map((g) => (
            <th key={g.group_id ?? g.group_name} style={{ ...PV.th, textAlign: "right" }}>
              {g.group_name}
            </th>
          ))}
        </tr>
        <tr>
          {groups.map((g) => (
            <th key={`p-${g.group_id ?? g.group_name}`}
              style={{ ...PV.th, textAlign: "right", fontWeight: 400, color: "#6b7280" }}>
              {level === "unit" ? levelNote : `${g.personnel_count}명`}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <PvRow label="1. 인 건 비" strong texts={groups.map(() => "")} />
        <PvRow label="가. 기본급" indent={1} values={cols.map((c) => c.base_pay)} />
        <PvRow label="나. 제수당" indent={1} values={pick((t) => t.allowance_total)} />
        {ALLOWANCE_CATEGORIES.map((cat, i) => (
          <PvRow key={cat.value} label={`${i + 1}) ${cat.label}`} indent={2}
            values={T.map((t) => (t.allowance_lines || []).find((a) => a.category === cat.value)?.amount || 0)} />
        ))}
        <PvRow label="다. 상여금" indent={1} values={cols.map((c) => c.bonus)} />
        <PvRow label="라. 퇴직급여충당금" indent={1} values={cols.map((c) => c.severance)} />
        <PvRow label="계" strong indent={1} values={pick((t) => t.labor_cost)} />

        <PvRow label="2. 경    비" strong texts={groups.map(() => "")} />
        <PvRow label="보 험 료" indent={1} texts={groups.map(() => "")} />
        {INSURANCE_LABELS.map(([k, lb]) => (
          <PvRow key={k} label={lb} indent={2} values={cols.map((c) => c.insurance?.[k] || 0)} />
        ))}
        <PvRow label="소계" strong indent={2} values={pick((t) => t.insurance_subtotal)} />
        <PvRow label="복리후생비" indent={1} texts={groups.map(() => "")} />
        {welfareLabels.map((label, i) => (
          <PvRow key={`w${label}`} label={`${KOREAN_ORDINALS[i] || "·"}. ${label}`} indent={2}
            values={T.map((t) => amountOf(t, 0, label))} />
        ))}
        <PvRow label="소계" strong indent={2} values={T.map((t) => sum(splitOthers(t)[0]))} />
        <PvRow label="기타" indent={1} texts={groups.map(() => "")} />
        {etcLabels.map((label, i) => (
          <PvRow key={`e${label}`} label={`${KOREAN_ORDINALS[i] || "·"}. ${label}`} indent={2}
            values={T.map((t) => amountOf(t, 1, label))} />
        ))}
        <PvRow label="소계" strong indent={2} values={T.map((t) => sum(splitOthers(t)[1]))} />
        <PvRow label="계" strong indent={1} values={pick((t) => t.expense_total)} />

        {level === "unit" ? (
          // 일위대가 시트는 여기서 끝난다 — 일반관리비·이윤·총원가·부가세 행은
          // 엑셀 양식에서도 월간·연간 시트에만 있다(양식 실측).
          <PvRow label="합계" strong values={pick((t) => t.labor_plus_expense)} />
        ) : (
          <>
            <PvRow label="3. 인건비 + 경비" strong values={pick((t) => t.labor_plus_expense)} />
            <PvRow label="4. 일반관리비" values={pick((t) => t.general_admin)} />
            <PvRow label="5. 이   윤" values={pick((t) => t.profit)} />
            <PvRow label="6. 총원가" strong values={pick((t) => t.total_cost)} />
            <PvRow label="7. 부가가치세" values={pick((t) => t.vat)} />
            {/* 월간은 10원 단위 절사된 값 — calc이 확정한 "final"을 엑셀과 같이 쓴다 */}
            <PvRow label={level === "monthly" ? "8. 합  계" : "8. 소  계"} strong
              values={pick((t) => t.final)} />
          </>
        )}
      </tbody>
    </table>
  );
}

function PreviewPanel(props) {
  const {
    preview, months, title, year, institution, hourlyWage, wageRegion,
    pensionRate, healthRate, careRate, employRate, injuryRate,
    generalAdminRate, profitRate, annualRounddown,
  } = props;
  const [sheet, setSheet] = useState("견적서");   // ✅ 표지가 기본 탭

  const groups = preview?.groups || [];
  const supply = groups.reduce((a, g) => a + Number(g.annual?.totals?.total_cost || 0), 0);
  const vat = groups.reduce((a, g) => a + Number(g.annual?.totals?.vat || 0), 0);
  const totalPersonnel = groups.reduce((a, g) => a + Number(g.personnel_count || 0), 0);
  const grand = Number(preview?.annual_grand_total || 0);
  const pct = (v) => `${Number(v || 0)}%`;

  return (
    <div style={PV.panel}>
      <div style={PV.head}>
        <span style={PV.title}>실시간 계산 미리보기</span>
        <span style={{ fontSize: 11, color: "#9ca3af", whiteSpace: "nowrap" }}>엑셀 다운로드와 같은 구조</span>
      </div>

      <div style={PV.tabBar}>
        {PREVIEW_SHEETS.map((s) => (
          <button key={s} type="button" style={PV.tab(sheet === s)} onClick={() => setSheet(s)}>{s}</button>
        ))}
      </div>

      <div style={PV.body}>
        {!preview ? (
          <div style={PV.empty}>시급·근무조건(인원수 / 주 N일 / 1일 N시간)을 입력하면 계산됩니다.</div>
        ) : sheet === "견적서" ? (
          <>
            <table style={PV.table}>
              <colgroup><col style={{ width: "38%" }} /><col /></colgroup>
              <tbody>
                <PvRow label="발주처" texts={[institution || "-"]} />
                <PvRow label="품명" texts={[title || "-"]} />
                <PvRow label="연도" texts={[`${year}년`]} />
                <PvRow label="계약기간" texts={[`${months}개월`]} />
                <PvRow label="인원" texts={[`${totalPersonnel}명`]} />
              </tbody>
            </table>

            {/* ✅ 엑셀 표지의 품명행(인원 / 수량(개월) / 단가 / 금액)과 같은 열 구조 */}
            <div style={PV.caption}>품 명</div>
            <table style={PV.table}>
              <colgroup>
                <col style={{ width: "34%" }} /><col style={{ width: "13%" }} />
                <col style={{ width: "17%" }} /><col style={{ width: "18%" }} /><col />
              </colgroup>
              <thead>
                <tr>
                  <th style={PV.th}>품 명</th>
                  <th style={{ ...PV.th, textAlign: "right" }}>인원</th>
                  <th style={{ ...PV.th, textAlign: "right" }}>수량<br />(개월)</th>
                  <th style={{ ...PV.th, textAlign: "right" }}>단가<br />(부가세포함)</th>
                  <th style={{ ...PV.th, textAlign: "right" }}>금액</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={PV.td}>{title || "-"}</td>
                  <PvCell text={`${totalPersonnel}명`} />
                  <PvCell text={`${months}`} />
                  <PvCell value={months ? grand / Number(months) : 0} />
                  <PvCell value={grand} strong />
                </tr>
              </tbody>
            </table>

            <div style={PV.caption}>조건별 연간 소계</div>
            <table style={PV.table}>
              <colgroup><col style={{ width: "56%" }} /><col /></colgroup>
              <thead>
                <tr><th style={PV.th}>조건</th><th style={{ ...PV.th, textAlign: "right" }}>연간 소계</th></tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <PvRow key={g.group_id ?? g.group_name}
                    label={`${g.group_name} (${g.personnel_count}명)`}
                    values={[g.annual?.totals?.final]} />
                ))}
              </tbody>
            </table>

            <div style={PV.caption}>합계</div>
            <table style={PV.table}>
              <colgroup><col style={{ width: "56%" }} /><col /></colgroup>
              <tbody>
                <PvRow label="공급가액" values={[supply]} />
                <PvRow label="부 가 세" values={[vat]} />
                <PvRow label="총    계" values={[grand]} strong />
              </tbody>
            </table>
            <div style={{ marginTop: 8, padding: "10px 12px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6, textAlign: "center" }}>
              <div style={{ fontSize: 11, color: "#1e40af", whiteSpace: "nowrap" }}>합계금액 (공급가액 + 부가세, 천원 단위 절사)</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#1e3a8a" }}>{won(grand)}</div>
            </div>
          </>
        ) : sheet === "기준단가" ? (
          <table style={PV.table}>
            <colgroup><col style={{ width: "56%" }} /><col /></colgroup>
            <thead>
              <tr><th style={PV.th}>항 목</th><th style={{ ...PV.th, textAlign: "right" }}>값</th></tr>
            </thead>
            <tbody>
              <PvRow label="시급" texts={[`${Number(hourlyWage || 0).toLocaleString()}원`]} />
              <PvRow label="생활임금 지자체" texts={[wageRegion || "-"]} />
              <PvRow label="계약개월수" texts={[`${months}개월`]} />
              <PvRow label="보험료율" strong texts={[""]} />
              <PvRow label="가. 국민연금" texts={[pct(pensionRate)]} indent={1} />
              <PvRow label="나. 건강보험" texts={[pct(healthRate)]} indent={1} />
              <PvRow label="다. 노인장기요양(건강보험료 대비)" texts={[pct(careRate)]} indent={1} />
              <PvRow label="라. 고용보험" texts={[pct(employRate)]} indent={1} />
              <PvRow label="마. 산재보험" texts={[pct(injuryRate)]} indent={1} />
              <PvRow label="관리비·이윤" strong texts={[""]} />
              <PvRow label="4. 일반관리비" texts={[pct(generalAdminRate)]} indent={1} />
              <PvRow label="5. 이윤" texts={[pct(profitRate)]} indent={1} />
              <PvRow label="연간 총원가 10원 단위 절사" texts={[annualRounddown ? "적용" : "미적용"]} indent={1} />
            </tbody>
          </table>
        ) : sheet === "일위대가" ? (
          <CostSheet groups={groups} level="unit" months={months} />
        ) : sheet === "월간" ? (
          <CostSheet groups={groups} level="monthly" months={months} />
        ) : (
          <>
            <CostSheet groups={groups} level="annual" months={months} />
            <div style={PV.caption}>9. 합계</div>
            <table style={PV.table}>
              <colgroup><col style={{ width: "58%" }} /><col /></colgroup>
              <tbody>
                <PvRow label="전 조건 소계 합 → 천원 단위 절사" value={grand} strong />
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
